/* eslint-disable */
import type { RuleType, Tag } from "../banklin/data.pb";
import type { DigitalWalletToken } from "../banklin/digital_wallet.pb";
import type { MissingRewardReport } from "../banklin/rewards.pb";
import type { TransactionEvent, TransactionReasonCode } from "../banklin/transaction.pb";
import type { Expiration } from "../cash/expiration.pb";
import type { Money } from "../common/currency.pb";
import type { InstrumentType } from "../common/instrument.pb";
import type { GlobalAddress } from "../common/location.pb";
import type { Merchant } from "../merchantein/merchant/merchant.pb";
import type { Alias } from "./alias.pb";
import type { Instrument } from "./api.pb";
import type { AppMessageData } from "./app_messaging.pb";
import type { BrowserInteraction } from "./browser_interaction.pb";
import type { BuildType, Platform } from "./common.pb";
import type { Customer, GovernmentId } from "./customer.pb";
import type { ReferralType } from "./data_referral_type.pb";
import type { DepositPreference } from "./deposit_preference.pb";
import type { InstrumentLink } from "./instrument_link.pb";
import type { InvestmentOrder } from "./investing/resources.pb";
import type { Orientation } from "./orientation.pb";
import type {
  Direction,
  PaymentFailureReason,
  PaymentMoneyMovingMechanism,
  PaymentPull,
  PaymentPush,
  PaymentState,
} from "./payment.pb";
import type { PaymentCard } from "./payment_card.pb";
import type { Role } from "./role.pb";
import type { ThreeDomainSecureRedirectUrl } from "./sca/three_domain_secure.pb";
import type { SignalsContext } from "./signal.pb";

export enum ConfigKey {
  FRONT_END_EMBER_S3_ASSET_SHA = "FRONT_END_EMBER_S3_ASSET_SHA",
  DEVELOPERS_DASHBOARD_S3_ASSET_SHA = "DEVELOPERS_DASHBOARD_S3_ASSET_SHA",
  SMS_LOGIN_ENABLED = "SMS_LOGIN_ENABLED",
  PASSWORDLESS_LOGIN_ENABLED = "PASSWORDLESS_LOGIN_ENABLED",
  SYSTEM_OFFLINE_PAGING_AMOUNT_CENTS = "SYSTEM_OFFLINE_PAGING_AMOUNT_CENTS",
  SYSTEM_OFFLINE_BLOCK_AMOUNT_CENTS = "SYSTEM_OFFLINE_BLOCK_AMOUNT_CENTS",
  NUMBER_OF_PERSONAL_CHARGEBACKS_TO_DENYLIST = "NUMBER_OF_PERSONAL_CHARGEBACKS_TO_DENYLIST",
  NUMBER_OF_PERSONAL_CHARGEBACKS_TO_DENYLIST_OLD_CUSTOMERS = "NUMBER_OF_PERSONAL_CHARGEBACKS_TO_DENYLIST_OLD_CUSTOMERS",
  REQUEST_SPAM_CONFIG_JSON = "REQUEST_SPAM_CONFIG_JSON",
  /** INVITE_EMAIL_SEND_IMMEDIATELY_COUNT - The number of invite emails to send immediately. */
  INVITE_EMAIL_SEND_IMMEDIATELY_COUNT = "INVITE_EMAIL_SEND_IMMEDIATELY_COUNT",
  /** INVITE_EMAIL_DELAY_SECONDS - The number of seconds to delay between sending each invitation email. */
  INVITE_EMAIL_DELAY_SECONDS = "INVITE_EMAIL_DELAY_SECONDS",
  /** INVITE_EMAIL_SUPPRESSION_THRESHOLD - If the number of invites is above this threshold, the invites will be suppressed. */
  INVITE_EMAIL_SUPPRESSION_THRESHOLD = "INVITE_EMAIL_SUPPRESSION_THRESHOLD",
  /** FACEBOOK_SHARE_PROFILE_IMAGE_DIMENSION - Dimension of the static profile image for Facebook and Open Graph consumers. */
  FACEBOOK_SHARE_PROFILE_IMAGE_DIMENSION = "FACEBOOK_SHARE_PROFILE_IMAGE_DIMENSION",
  /** APP_MESSAGE_WAKEUP_WAIT_TIME_MS - Number of milliseconds to wait for each app message notification sent. */
  APP_MESSAGE_WAKEUP_WAIT_TIME_MS = "APP_MESSAGE_WAKEUP_WAIT_TIME_MS",
  /** APP_MESSAGE_WAKEUP_BATCH_SIZE - Number of app message notifications to send at a time. */
  APP_MESSAGE_WAKEUP_BATCH_SIZE = "APP_MESSAGE_WAKEUP_BATCH_SIZE",
  /** COLLEGE_CUSTOMER_LIFETIME_LIMIT_AMOUNT_CENTS - The maximum amount in cents that the college bonus account can send to a single customer. */
  COLLEGE_CUSTOMER_LIFETIME_LIMIT_AMOUNT_CENTS = "COLLEGE_CUSTOMER_LIFETIME_LIMIT_AMOUNT_CENTS",
  /** COLLEGE_BONUS_DAILY_LIMIT_AMOUNT_CENTS - The maximum amount in cents that the college bonus account can send in a day. */
  COLLEGE_BONUS_DAILY_LIMIT_AMOUNT_CENTS = "COLLEGE_BONUS_DAILY_LIMIT_AMOUNT_CENTS",
  /**
   * COLLEGE_BONUS_PAYMENT_LIMIT_MAP - The limit of amount cents that each college program can spend in a day and on each customer
   *  for their lifetime as a json string.
   */
  COLLEGE_BONUS_PAYMENT_LIMIT_MAP = "COLLEGE_BONUS_PAYMENT_LIMIT_MAP",
  /** NAME_CHARACTER_ALLOWLIST - A list of characters that are allowed in names. */
  NAME_CHARACTER_ALLOWLIST = "NAME_CHARACTER_ALLOWLIST",
  /** NAME_CHARACTER_IGNORE_LIST - A list of characters that are not allowed in names whose presence should not log a warning. */
  NAME_CHARACTER_IGNORE_LIST = "NAME_CHARACTER_IGNORE_LIST",
  /** BUSINESS_REFERRAL_TEMPLATE_NAME - The template name that we use to send in-app messages about the business referral program. */
  BUSINESS_REFERRAL_TEMPLATE_NAME = "BUSINESS_REFERRAL_TEMPLATE_NAME",
  /** TWITTER_SHARE_PROFILE_IMAGE_DIMENSION - Dimension of the static profile image for Twitter. */
  TWITTER_SHARE_PROFILE_IMAGE_DIMENSION = "TWITTER_SHARE_PROFILE_IMAGE_DIMENSION",
  /** MAXIMUM_REFERRAL_BOUNTIES_PER_DAY - A hard limit on the number of referrals a customer or instrument can receive in a day. */
  MAXIMUM_REFERRAL_BOUNTIES_PER_DAY = "MAXIMUM_REFERRAL_BOUNTIES_PER_DAY",
  /** ENTITY_SYNCING_REVISION_NUMBER - Incrementing this revision number forces all apps to resync entities from scratch. */
  ENTITY_SYNCING_REVISION_NUMBER = "ENTITY_SYNCING_REVISION_NUMBER",
  /**
   * MAX_CREDIT_CARD_PROMPTS - The maximum number of times to prompt a user that already has a debit card linked to enter
   * a credit card during payment flow in the app.
   *
   * @deprecated
   */
  MAX_CREDIT_CARD_PROMPTS = "MAX_CREDIT_CARD_PROMPTS",
  /**
   * APP_SITE_ASSOCIATION_FILE - The filename for apple-app-site-association to be used. Allows quick switching without deploy.
   * Directory is defined by AppleAppSiteAssociationResources.
   *
   * @deprecated
   */
  APP_SITE_ASSOCIATION_FILE = "APP_SITE_ASSOCIATION_FILE",
  /** STORED_VALUE_RISK - This holds a bundle of thresholds and configurations to control the stored value launch. */
  STORED_VALUE_RISK = "STORED_VALUE_RISK",
  /** DETAIL_ICON_URL_VISA - Detail icon url for Visa credit cards. */
  DETAIL_ICON_URL_VISA = "DETAIL_ICON_URL_VISA",
  /** DETAIL_ICON_URL_MASTER_CARD - Detail icon url for MasterCardV credit cards. */
  DETAIL_ICON_URL_MASTER_CARD = "DETAIL_ICON_URL_MASTER_CARD",
  /** DETAIL_ICON_URL_AMEX - Detail icon url for Amex credit cards. */
  DETAIL_ICON_URL_AMEX = "DETAIL_ICON_URL_AMEX",
  /** DETAIL_ICON_URL_DISCOVER - Detail icon url for Discover credit cards. */
  DETAIL_ICON_URL_DISCOVER = "DETAIL_ICON_URL_DISCOVER",
  /** DETAIL_ICON_URL_OTHER - Detail icon url for other credit cards. */
  DETAIL_ICON_URL_OTHER = "DETAIL_ICON_URL_OTHER",
  /** TAB_DETAIL_ICON_URL_VISA - Tab view Detail icon url for Visa credit cards. */
  TAB_DETAIL_ICON_URL_VISA = "TAB_DETAIL_ICON_URL_VISA",
  /** TAB_DETAIL_ICON_URL_MASTER_CARD - Tab view Detail icon url for MasterCardV credit cards. */
  TAB_DETAIL_ICON_URL_MASTER_CARD = "TAB_DETAIL_ICON_URL_MASTER_CARD",
  /** TAB_DETAIL_ICON_URL_AMEX - Tab view Detail icon url for Amex credit cards. */
  TAB_DETAIL_ICON_URL_AMEX = "TAB_DETAIL_ICON_URL_AMEX",
  /** TAB_DETAIL_ICON_URL_DISCOVER - Tab view Detail icon url for Discover credit cards. */
  TAB_DETAIL_ICON_URL_DISCOVER = "TAB_DETAIL_ICON_URL_DISCOVER",
  /** TAB_DETAIL_ICON_URL_OTHER - Tab view Detail icon url for other credit cards. */
  TAB_DETAIL_ICON_URL_OTHER = "TAB_DETAIL_ICON_URL_OTHER",
  /**
   * MAX_DISTINCT_CUSTOMERS_CAN_CASH_OUT_TO_SAME_INSTRUMENT - Controls how many distinct customers can cash out using the same instrument in order to avoid
   * fraud.
   */
  MAX_DISTINCT_CUSTOMERS_CAN_CASH_OUT_TO_SAME_INSTRUMENT = "MAX_DISTINCT_CUSTOMERS_CAN_CASH_OUT_TO_SAME_INSTRUMENT",
  /**
   * DISABLE_SNAPCHAT - Shuts down access to Snapchat if they start misbehaving and overwhelming our service. In the
   * past a Snapchat thundering herd has caused outages.
   */
  DISABLE_SNAPCHAT = "DISABLE_SNAPCHAT",
  /** MERCHANTS_FOR_SIMULATION - List of merchants against whom a transaction can be simulated in staging. */
  MERCHANTS_FOR_SIMULATION = "MERCHANTS_FOR_SIMULATION",
  /** HASHED_ALIAS_SYNC_BATCH_SIZE - Number of hashed aliases to sync in a single batch. */
  HASHED_ALIAS_SYNC_BATCH_SIZE = "HASHED_ALIAS_SYNC_BATCH_SIZE",
  /**
   * APP_RETRY_INTERVALS - A comma-separated list of durations (in ms) that the apps use to retry attempted payments and
   * transfers. See OfflineConfig.retry_intervals in common.proto for more details.
   */
  APP_RETRY_INTERVALS = "APP_RETRY_INTERVALS",
  /**
   * CANCEL_OFFLINE_RETRIED_PAYMENT_SECONDS - Amount of time after a payment was originally attempted by the app to cancel it when we
   * receive the retry request.
   */
  CANCEL_OFFLINE_RETRIED_PAYMENT_SECONDS = "CANCEL_OFFLINE_RETRIED_PAYMENT_SECONDS",
  /** BITCOIN_TRANSACTION_CUSTOMER_ACCENT_COLOR - The accent color of the bitcoin customer, used to render the activity feed. */
  BITCOIN_TRANSACTION_CUSTOMER_ACCENT_COLOR = "BITCOIN_TRANSACTION_CUSTOMER_ACCENT_COLOR",
  /** YODLEE_UPDATE_INSTITUTIONS_ENABLED - Enables the YodleeInstitutionUpdateInternalApi. */
  YODLEE_UPDATE_INSTITUTIONS_ENABLED = "YODLEE_UPDATE_INSTITUTIONS_ENABLED",
  /**
   * STORED_VALUE_BALANCE_RECONCILIATION_HOURLY_JOB - Stores the last success time for the StoredValueBalanceReconciliation job.
   * Settles balances between the base backing bank accounts that Cash App uses.
   * The bank accounts are defined in BankAccountMoneyMover.BankAccount
   * FBO(WELLS_CASH, "wells") and BA(WELLS_PLATFORM, "wells_square")
   */
  STORED_VALUE_BALANCE_RECONCILIATION_HOURLY_JOB = "STORED_VALUE_BALANCE_RECONCILIATION_HOURLY_JOB",
  /**
   * VOLUME_COLLECTION_JOB_FINISHED - Stores the last time the WF volume collection job was finished.
   * This is used to determine if we should re-run the job in an automated way.
   */
  VOLUME_COLLECTION_JOB_FINISHED = "VOLUME_COLLECTION_JOB_FINISHED",
  /**
   * DEMOGRAPHICS_COLLECTION_JOB_FINISHED - Stores the last time the WF demographics collection job was finished.
   * This is used to determine if we should re-run the job in an automated way.
   */
  DEMOGRAPHICS_COLLECTION_JOB_FINISHED = "DEMOGRAPHICS_COLLECTION_JOB_FINISHED",
  /**
   * VOLUME_REPORT_SENT - Stores the last time the WF volume report was sent.
   * This is used to determine if we should re-run the report generation job in an automated way.
   */
  VOLUME_REPORT_SENT = "VOLUME_REPORT_SENT",
  /**
   * DEMOGRAPHICS_REPORT_SENT - Stores the last time the WF demographics report was sent.
   * This is used to determine if we should re-run the report generation job in an automated way.
   */
  DEMOGRAPHICS_REPORT_SENT = "DEMOGRAPHICS_REPORT_SENT",
  /**
   * WELLS_FARGO_MONTHLY_LAST_SENT_DEMOGRAPHICS_REPORT - Stores the last demographics report number of the total demographics reports that was sent
   * to wells fargo. These numbers range from 0 to numberOfChunks set on LaunchDarkly.
   */
  WELLS_FARGO_MONTHLY_LAST_SENT_DEMOGRAPHICS_REPORT = "WELLS_FARGO_MONTHLY_LAST_SENT_DEMOGRAPHICS_REPORT",
  /**
   * WELLS_FARGO_MONTHLY_LAST_SENT_VOLUME_REPORT - Stores the last volume report number of the total volume reports that was sent to wells fargo.
   * These numbers range from 0 to numberOfChunks set on LaunchDarkly.
   */
  WELLS_FARGO_MONTHLY_LAST_SENT_VOLUME_REPORT = "WELLS_FARGO_MONTHLY_LAST_SENT_VOLUME_REPORT",
}

export enum PaymentInstrument {
  /** CARD - A debit card. */
  CARD = "CARD",
  /** US_BANK_ACCOUNT - A US bank account with ABA Routing Number and Account Number. */
  US_BANK_ACCOUNT = "US_BANK_ACCOUNT",
  /** SQUARE - Square's money. */
  SQUARE = "SQUARE",
  /** BALANCE - An Octopus Balance. */
  BALANCE = "BALANCE",
}

/** A description of possible actions or criteria that lead to a customer becoming heavy. */
export enum HeavyReason {
  /** ENABLING_CRYPTOCURRENCY - Action taken to enable a BTC balance. */
  ENABLING_CRYPTOCURRENCY = "ENABLING_CRYPTOCURRENCY",
  /** ENABLING_INVESTING - Action taken to create a investing brokerage account. */
  ENABLING_INVESTING = "ENABLING_INVESTING",
  /** BORROWED_LOAN - The customer borrowed a loan and has become a lending customer. */
  BORROWED_LOAN = "BORROWED_LOAN",
}

/** Defines the type of payment this transfer is funding. */
export enum PaymentFundingType {
  /** CROSS_BORDER - This represents if the transfer was a cash-in for funding a cross-border payment. */
  CROSS_BORDER = "CROSS_BORDER",
}

export enum DisputeReason {
  /**
   * USER_SUPPLIED_REASON - The reason for the unauthorized payment cannot be categorized below,
   * and has been supplied as a manual comment by the customer
   */
  USER_SUPPLIED_REASON = "USER_SUPPLIED_REASON",
  /** NOT_SENT_BY_CUSTOMER - The customer indicates they did not initiate the payment. Accidentally or otherwise. */
  NOT_SENT_BY_CUSTOMER = "NOT_SENT_BY_CUSTOMER",
  /** ACCIDENTAL - The customer indicates the payment was initiated accidentally by their own error. */
  ACCIDENTAL = "ACCIDENTAL",
  /** AMOUNT_INCORRECT - The payment is intended. But the amount sent differs from the customer's intent. */
  AMOUNT_INCORRECT = "AMOUNT_INCORRECT",
  /** DUPLICATED - The payment is a duplicate payment of what was an intended payment by the customer. */
  DUPLICATED = "DUPLICATED",
  /** INCORRECT_RECIPIENT - The payment was intended. But was sent to the unintended recipient. */
  INCORRECT_RECIPIENT = "INCORRECT_RECIPIENT",
  /** CHARGEBACK - The sender contacted their issuer to charge back the payment. */
  CHARGEBACK = "CHARGEBACK",
  /** NOT_RECEIVED - The customer indicates the payment was not received by the recipient. */
  NOT_RECEIVED = "NOT_RECEIVED",
  /** MERCHANT_ISSUE - The customer is unsatisfied with the goods or services purchased. */
  MERCHANT_ISSUE = "MERCHANT_ISSUE",
}

export enum DisputeRecoveryStatus {
  NEW_DISPUTE = "NEW_DISPUTE",
  IN_PROCESS = "IN_PROCESS",
  COMPLETE = "COMPLETE",
}

/** CashInstrumentType represents the types of instruments that can be used within Cash. */
export enum CashCustomerAssetType {
  ADDRESS = "ADDRESS",
}

/**
 * Who is calling into Cash. The same event triggered by different user agent
 * types may yield different behavior. For example, cards linked by modern Cash
 * apps may suppress notification emails; cards linked by 1.x apps do not.
 *
 * As a general rule, this enum differentiates user agents by their
 * authentication mechanism.
 */
export enum UserAgentType {
  /** APP_V1 - Cash 1.x apps that send payments by sending email. */
  APP_V1 = "APP_V1",
  /** APP - Cash apps that sent payments by API. Includes Cash 2.0 and later. */
  APP = "APP",
  /**
   * PLATFORM_CLIENT - Platform API calls made by a third-party application. These user agents
   * authenticate with a client secret.
   */
  PLATFORM_CLIENT = "PLATFORM_CLIENT",
  PLATFORM_V2_CLIENT = "PLATFORM_V2_CLIENT",
  /**
   * PLATFORM_CUSTOMER - Platform API calls made by a third-party service or application on behalf
   * of a specific customer. These user agents authenticate with either a client
   * secret or an access token.
   */
  PLATFORM_CUSTOMER = "PLATFORM_CUSTOMER",
  PLATFORM_V2_CUSTOMER = "PLATFORM_V2_CUSTOMER",
  /**
   * INTERNAL_API - Internal API to Regulator, Frisky etc. These user agents authenticate with
   * client certificates.
   */
  INTERNAL_API = "INTERNAL_API",
  /**
   * BROWSER - Web browsers interacting with Square-hosted web sites. Typically
   * authenticated with a cookie (such as for logged-in web settings) or with a
   * secret token in an URL (such as for payment flows.)
   *
   * This doesn't cover web-based third-party applications that communicate with
   * Cash via platform APIs.
   *
   * It does cover third-party apps that use Square-hosted web pages for signup
   * and payment completion flows. For example, when an app uses a web view to
   * resolve blockers.
   */
  BROWSER = "BROWSER",
  /** INCOMING_EMAIL - Email sent to cash@square.com, request@square.com, etc. */
  INCOMING_EMAIL = "INCOMING_EMAIL",
  /** ADMIN - The Cash admin web dashboard and APIs. */
  ADMIN = "ADMIN",
  /** DYN - External API to Dyn for email delivery updates. */
  DYN = "DYN",
  /** TWILIO - External API to Twilio for incoming SMS messages and delivery updates. */
  TWILIO = "TWILIO",
  /** PLATFORM_DASHBOARD - * Platform developers dashboard. Authenticated with a session cookie. */
  PLATFORM_DASHBOARD = "PLATFORM_DASHBOARD",
  EVENTLY = "EVENTLY",
}

export enum LimitCategory {
  PERSONAL_SEND_AMOUNT_BY_ACCOUNT_WEEKLY = "PERSONAL_SEND_AMOUNT_BY_ACCOUNT_WEEKLY",
  PERSONAL_SEND_AMOUNT_BY_DEBIT_CARD_WEEKLY = "PERSONAL_SEND_AMOUNT_BY_DEBIT_CARD_WEEKLY",
  PERSONAL_SEND_AMOUNT_BY_CREDIT_CARD_WEEKLY = "PERSONAL_SEND_AMOUNT_BY_CREDIT_CARD_WEEKLY",
  PERSONAL_SEND_AMOUNT_BY_BANK_WEEKLY = "PERSONAL_SEND_AMOUNT_BY_BANK_WEEKLY",
  PERSONAL_SEND_AMOUNT_BY_ACCOUNT_MONTHLY = "PERSONAL_SEND_AMOUNT_BY_ACCOUNT_MONTHLY",
  PERSONAL_SEND_AMOUNT_BY_DEBIT_CARD_MONTHLY = "PERSONAL_SEND_AMOUNT_BY_DEBIT_CARD_MONTHLY",
  PERSONAL_SEND_AMOUNT_BY_CREDIT_CARD_MONTHLY = "PERSONAL_SEND_AMOUNT_BY_CREDIT_CARD_MONTHLY",
  PERSONAL_SEND_AMOUNT_BY_BANK_MONTHLY = "PERSONAL_SEND_AMOUNT_BY_BANK_MONTHLY",
  BUSINESS_SEND_AMOUNT_BY_ACCOUNT = "BUSINESS_SEND_AMOUNT_BY_ACCOUNT",
  BUSINESS_SEND_AMOUNT_BY_DEBIT_CARD = "BUSINESS_SEND_AMOUNT_BY_DEBIT_CARD",
  BUSINESS_SEND_AMOUNT_BY_CREDIT_CARD = "BUSINESS_SEND_AMOUNT_BY_CREDIT_CARD",
  RECEIVE_AMOUNT_BY_ACCOUNT = "RECEIVE_AMOUNT_BY_ACCOUNT",
  RECEIVE_AMOUNT_BY_DEBIT_CARD = "RECEIVE_AMOUNT_BY_DEBIT_CARD",
  OUTSTANDING_BILL_AMOUNT = "OUTSTANDING_BILL_AMOUNT",
  RECEIVE_PAYMENTS_COUNT = "RECEIVE_PAYMENTS_COUNT",
  BTC_BUY_AMOUNT_BY_ACCOUNT_WEEKLY = "BTC_BUY_AMOUNT_BY_ACCOUNT_WEEKLY",
  BTC_WITHDRAW_AMOUNT_BY_ACCOUNT_WEEKLY = "BTC_WITHDRAW_AMOUNT_BY_ACCOUNT_WEEKLY",
  BTC_WITHDRAW_AMOUNT_BY_ACCOUNT_DAILY = "BTC_WITHDRAW_AMOUNT_BY_ACCOUNT_DAILY",
  CASH_OUT = "CASH_OUT",
  /**
   * DEBIT_CASH_IN_WEEKLY - debit cash in limits fall under PERSONAL_SEND_AMOUNT_BY_DEBIT_CARD
   *
   * @deprecated
   */
  DEBIT_CASH_IN_WEEKLY = "DEBIT_CASH_IN_WEEKLY",
  /** @deprecated */
  DEBIT_CASH_IN_MONTHLY = "DEBIT_CASH_IN_MONTHLY",
  PREFUNDED_CASH_IN_WEEKLY = "PREFUNDED_CASH_IN_WEEKLY",
  PREFUNDED_CASH_IN_MONTHLY = "PREFUNDED_CASH_IN_MONTHLY",
  SEND_RECEIVE_BY_ACCOUNT_WEEKLY = "SEND_RECEIVE_BY_ACCOUNT_WEEKLY",
  SEND_RECEIVE_BY_ACCOUNT_MONTHLY = "SEND_RECEIVE_BY_ACCOUNT_MONTHLY",
  SEND_RECEIVE_BY_DEBIT_CARD_WEEKLY = "SEND_RECEIVE_BY_DEBIT_CARD_WEEKLY",
  SEND_RECEIVE_BY_DEBIT_CARD_MONTHLY = "SEND_RECEIVE_BY_DEBIT_CARD_MONTHLY",
  SEND_RECEIVE_BY_CREDIT_CARD_WEEKLY = "SEND_RECEIVE_BY_CREDIT_CARD_WEEKLY",
  SEND_RECEIVE_BY_CREDIT_CARD_MONTHLY = "SEND_RECEIVE_BY_CREDIT_CARD_MONTHLY",
  SEND_RECEIVE_BY_BANK_WEEKLY = "SEND_RECEIVE_BY_BANK_WEEKLY",
  SEND_RECEIVE_BY_BANK_MONTHLY = "SEND_RECEIVE_BY_BANK_MONTHLY",
}

/** The program a particular reward code is part of */
export enum RewardProgram {
  /** FIVE_BY_FIVE - 5 x 5 promotion where both the referrer and referee get $5. */
  FIVE_BY_FIVE = "FIVE_BY_FIVE",
  /** TEN_BY_TEN - 10 x 10 promotion where both the referrer and referee get $10. */
  TEN_BY_TEN = "TEN_BY_TEN",
  /** TEN_BY_FIVE - 10 x 5 promotion where the referrer gets $10 and referee gets $5. */
  TEN_BY_FIVE = "TEN_BY_FIVE",
  /** TWENTY_BY_FIVE - 20 x 5 promotion where the referrer gets $20 and referee gets $5. */
  TWENTY_BY_FIVE = "TWENTY_BY_FIVE",
  /** TWENTY_FIVE_BY_FIVE - 25 x 5 promotion where the referrer gets $25 and referee gets $5. */
  TWENTY_FIVE_BY_FIVE = "TWENTY_FIVE_BY_FIVE",
  /** FIFTY_BY_FIVE - 50 x 5 promotion where the referrer gets $50 and referee gets $5. */
  FIFTY_BY_FIVE = "FIFTY_BY_FIVE",
  /** FIFTEEN_BY_FIVE - 15 x 5 promotion where the referrer gets $15 and referee gets $5. */
  FIFTEEN_BY_FIVE = "FIFTEEN_BY_FIVE",
  /** FIVE_BY_TEN - 5 x 10 promotion where the referrer gets $5 and referee gets $10. */
  FIVE_BY_TEN = "FIVE_BY_TEN",
  /** TEN_BY_TEN_REPLICATING_1 - 10 x 10 promotion that can replicate to the referee.  For trial area 1. */
  TEN_BY_TEN_REPLICATING_1 = "TEN_BY_TEN_REPLICATING_1",
  /** TEN_BY_TEN_REPLICATING_2 - 10 x 10 promotion that can replicate to the referee.  For trial area 2. */
  TEN_BY_TEN_REPLICATING_2 = "TEN_BY_TEN_REPLICATING_2",
  /** TEN_BY_TEN_REPLICATING_3 - 10 x 10 promotion that can replicate to the referee.  For trial area 3. */
  TEN_BY_TEN_REPLICATING_3 = "TEN_BY_TEN_REPLICATING_3",
  /** TEN_BY_TEN_REPLICATING_4 - 10 x 10 promotion that can replicate to the referee.  For trial area 4. */
  TEN_BY_TEN_REPLICATING_4 = "TEN_BY_TEN_REPLICATING_4",
  /** TEN_BY_TEN_REPLICATING_5 - 10 x 10 promotion that can replicate to the referee.  For trial area 5. */
  TEN_BY_TEN_REPLICATING_5 = "TEN_BY_TEN_REPLICATING_5",
  /** TEN_BY_TEN_REPLICATING_6 - 10 x 10 promotion that can replicate to the referee.  For trial area 6. */
  TEN_BY_TEN_REPLICATING_6 = "TEN_BY_TEN_REPLICATING_6",
  /** TEN_BY_TEN_REPLICATING_7 - 10 x 10 promotion that can replicate to the referee.  For trial area 7. */
  TEN_BY_TEN_REPLICATING_7 = "TEN_BY_TEN_REPLICATING_7",
  /** TEN_BY_TEN_REPLICATING_8 - 10 x 10 promotion that can replicate to the referee.  For trial area 8. */
  TEN_BY_TEN_REPLICATING_8 = "TEN_BY_TEN_REPLICATING_8",
  /** TEN_BY_TEN_REPLICATING_9 - 10 x 10 promotion that can replicate to the referee.  For trial area 9. */
  TEN_BY_TEN_REPLICATING_9 = "TEN_BY_TEN_REPLICATING_9",
  /** TEN_BY_TEN_REPLICATING_10 - 10 x 10 promotion that can replicate to the referee.  For trial area 10. */
  TEN_BY_TEN_REPLICATING_10 = "TEN_BY_TEN_REPLICATING_10",
  /** TEN_BY_TEN_REPLICATING_11 - 10 x 10 promotion that can replicate to the referee.  For trial area 11. */
  TEN_BY_TEN_REPLICATING_11 = "TEN_BY_TEN_REPLICATING_11",
  /** TEN_BY_TEN_REPLICATING_12 - 10 x 10 promotion that can replicate to the referee.  For trial area 12. */
  TEN_BY_TEN_REPLICATING_12 = "TEN_BY_TEN_REPLICATING_12",
  /** TEN_BY_TEN_REPLICATING_13 - 10 x 10 promotion that can replicate to the referee.  For trial area 13. */
  TEN_BY_TEN_REPLICATING_13 = "TEN_BY_TEN_REPLICATING_13",
  /** TEN_BY_TEN_REPLICATING_14 - 10 x 10 promotion that can replicate to the referee.  For trial area 14. */
  TEN_BY_TEN_REPLICATING_14 = "TEN_BY_TEN_REPLICATING_14",
  /** TEN_BY_TEN_REPLICATING_15 - 10 x 10 promotion that can replicate to the referee.  For trial area 15. */
  TEN_BY_TEN_REPLICATING_15 = "TEN_BY_TEN_REPLICATING_15",
  /** FIVE_BY_FIFTEEN - 5 x 15 promotion where the referrer gets $5 and referee gets $15. */
  FIVE_BY_FIFTEEN = "FIVE_BY_FIFTEEN",
  /** TEN_BY_FIFTEEN - 10 x 15 promotion where the referrer gets $10 and referee gets $15. */
  TEN_BY_FIFTEEN = "TEN_BY_FIFTEEN",
  /** FIFTEEN_BY_TEN - 15 x 10 promotion where the referrer gets $15 and referee gets $10. */
  FIFTEEN_BY_TEN = "FIFTEEN_BY_TEN",
  /** FIFTEEN_BY_FIFTEEN - 15 x 15 promotion where the referrer gets $15 and referee gets $15. */
  FIFTEEN_BY_FIFTEEN = "FIFTEEN_BY_FIFTEEN",
  /** ZERO_BY_FIVE - 0 x 5 promotion where the referrer gets $0 and the referee gets $5. */
  ZERO_BY_FIVE = "ZERO_BY_FIVE",
  /** ZERO_BY_TEN - 0 x 10 promotion where the referrer gets $0 and the referee gets $10. */
  ZERO_BY_TEN = "ZERO_BY_TEN",
  /** THIRTY_BY_FIFTEEN - 30 x 15 promotion where the referrer gets $30 and the referee gets $15. */
  THIRTY_BY_FIFTEEN = "THIRTY_BY_FIFTEEN",
  /** THIRTY_BY_TEN - 30 x 10 promotion where the referrer gets $30 and the referee gets $10. */
  THIRTY_BY_TEN = "THIRTY_BY_TEN",
  /** TWENTY_FIVE_BY_FIFTEEN - 25 x 15 promotion where the referrer gets $25 and the referee gets $15. */
  TWENTY_FIVE_BY_FIFTEEN = "TWENTY_FIVE_BY_FIFTEEN",
  /** TWENTY_FIVE_BY_TEN - 25 x 10 promotion where the referrer gets $25 and the referee gets $10. */
  TWENTY_FIVE_BY_TEN = "TWENTY_FIVE_BY_TEN",
  /** TWENTY_BY_FIFTEEN - 20 x 15 promotion where the referrer gets $20 and the referee gets $15. */
  TWENTY_BY_FIFTEEN = "TWENTY_BY_FIFTEEN",
  /** TWENTY_BY_TEN - 20 x 10 promotion where the referrer gets $20 and the referee gets $10. */
  TWENTY_BY_TEN = "TWENTY_BY_TEN",
  /** THIRTY_BY_FIVE - 30 x 5 promotion where the referrer gets $30 and the referee gets $5. */
  THIRTY_BY_FIVE = "THIRTY_BY_FIVE",
  /** ZERO_BY_FIFTEEN - 0 x 15 promotion where the referrer gets $0 and the referee gets $15. */
  ZERO_BY_FIFTEEN = "ZERO_BY_FIFTEEN",
  /** ONE_BY_TEN - 1 x 10 promotion where the referrer gets $1 and the referee gets $10. */
  ONE_BY_TEN = "ONE_BY_TEN",
}

export enum PostalCodeSource {
  /** CARD_LINK - The postal code was entered as part of linking a card. */
  CARD_LINK = "CARD_LINK",
  /** IDENTITY_VERIFICATION - The postal code was provided by IDV service during identity verification. */
  IDENTITY_VERIFICATION = "IDENTITY_VERIFICATION",
  /** PHONE_NUMBER - The postal code was determined from a phone number's area code. */
  PHONE_NUMBER = "PHONE_NUMBER",
  /** MANUAL_ENTRY - The postal code was manually manually entered by the customer in the app or web. */
  MANUAL_ENTRY = "MANUAL_ENTRY",
  /** MANUAL_OVERRIDE - The postal code was manually overriden by oncall or support and should not changed. */
  MANUAL_OVERRIDE = "MANUAL_OVERRIDE",
  /** GOVERNMENT_ID - The postal code was read from a customer's government ID. */
  GOVERNMENT_ID = "GOVERNMENT_ID",
}

/** Reason for showing an explicit confirm blocker */
export enum ExplicitConfirmationReason {
  /**
   * CASHTAG_NOT_IN_CONTACTS - Confirm payments sent to cashtags that are not in a customer's contacts or activity.
   *
   * @deprecated
   */
  CASHTAG_NOT_IN_CONTACTS = "CASHTAG_NOT_IN_CONTACTS",
  /**
   * CASHTAG_NOT_IN_CONTACTS_2 - Same as previous, with the difference that the app asked the customer to confirm adding the
   * cashtag as a recipient and no ConfirmBlocker should be added.
   *
   * @deprecated
   */
  CASHTAG_NOT_IN_CONTACTS_2 = "CASHTAG_NOT_IN_CONTACTS_2",
  /** @deprecated */
  SUSPECTED_RECYCLED_PHONE_NUMBER = "SUSPECTED_RECYCLED_PHONE_NUMBER",
  /**
   * PAYMENT_ABOVE_200_DOLLARS - Confirm payments above $200 if the sender/recipient have not had a $200+ payment paid out.
   *
   * @deprecated
   */
  PAYMENT_ABOVE_200_DOLLARS = "PAYMENT_ABOVE_200_DOLLARS",
  /**
   * PAYMENT_ABOVE_200_DOLLARS_RELAXED - Confirm payments above $200 if the sender/recipient have not had any successful payment.
   *
   * @deprecated
   */
  PAYMENT_ABOVE_200_DOLLARS_RELAXED = "PAYMENT_ABOVE_200_DOLLARS_RELAXED",
  /** INACTIVE_RECIPIENT - Confirm payments sent to recipients who may not have access to their account. */
  INACTIVE_RECIPIENT = "INACTIVE_RECIPIENT",
  /**
   * RECIPIENT_AUTO_CASH_OUT_NO_LINKED_INSTRUMENT - Confirm payment with recipient who may want to cash out without a valid, linked instrument.
   *
   * @deprecated
   */
  RECIPIENT_AUTO_CASH_OUT_NO_LINKED_INSTRUMENT = "RECIPIENT_AUTO_CASH_OUT_NO_LINKED_INSTRUMENT",
  /** SUSPECTED_MISDIRECTED_PAYMENT - Confirm payment (with recipient) if we suspect that it's a misdirected payment. */
  SUSPECTED_MISDIRECTED_PAYMENT = "SUSPECTED_MISDIRECTED_PAYMENT",
  /** FORCED_CONFIRMATION - Confirm payment (with recipient) without having a specified reason. */
  FORCED_CONFIRMATION = "FORCED_CONFIRMATION",
  /** BILL_FROM_OUTSIDE_CONTACTS - Confirm bills sent from customers not in another customer's contacts or activity */
  BILL_FROM_OUTSIDE_CONTACTS = "BILL_FROM_OUTSIDE_CONTACTS",
  /** REQUIRES_PREVIOUS_HISTORY - Payment confirmations cannot be done from the web unless customers have previous history. */
  REQUIRES_PREVIOUS_HISTORY = "REQUIRES_PREVIOUS_HISTORY",
  /** SUSPECTED_SCAM - Confirm bills (with sender) if we suspect that it's scam. */
  SUSPECTED_SCAM = "SUSPECTED_SCAM",
}

export enum CustomizationDeclineReason {
  OFFENSIVE = "OFFENSIVE",
  MISMATCH = "MISMATCH",
  RISK = "RISK",
  OTHER = "OTHER",
  NEED_GOVERNMENT_ID = "NEED_GOVERNMENT_ID",
}

export enum ManualInterventionReason {
  /** SHAZAM_INVALID_CHARACTERS - Shazam declined card order because card holder's name has unsupported special characters. */
  SHAZAM_INVALID_CHARACTERS = "SHAZAM_INVALID_CHARACTERS",
}

export enum CardVerificationMethod {
  QR_CODE = "QR_CODE",
  CVV_EXPIRATION = "CVV_EXPIRATION",
}

export enum AmendCustomizationReason {
  UNDELIVERABLE_ADDRESS = "UNDELIVERABLE_ADDRESS",
  ADDRESS_FAILED_DELIVERABILITY_VERIFICATION = "ADDRESS_FAILED_DELIVERABILITY_VERIFICATION",
}

export enum AddressServiceVerificationStatus {
  /**
   * UNKNOWN - We have either not tried to verify the address, or the address providers were unavailable when
   * we did try.
   */
  UNKNOWN = "UNKNOWN",
  VERIFICATION_SUCCEEDED = "VERIFICATION_SUCCEEDED",
  VERIFICATION_FAILED = "VERIFICATION_FAILED",
  /** VERIFICATION_FAILED_PARTIAL - Address wasn't wrong but was missing something for our requirements. (e.g. unit #) */
  VERIFICATION_FAILED_PARTIAL = "VERIFICATION_FAILED_PARTIAL",
  /** CUSTOMER_OVERRIDE - Address service failed the verification, but customer opted to use it anyway. */
  CUSTOMER_OVERRIDE = "CUSTOMER_OVERRIDE",
}

export enum CardDeliveryState {
  ORDERED = "ORDERED",
  SHIPPED = "SHIPPED",
  DELIVERED = "DELIVERED",
  UNDELIVERABLE = "UNDELIVERABLE",
}

export enum CashAdvanceState {
  /** ACTIVE - Cash has been advanced but not repayed. */
  ACTIVE = "ACTIVE",
  /** CLOSED - Cash advance has been repayed in full. */
  CLOSED = "CLOSED",
  /** OVERDUE - A repayment has failed. */
  OVERDUE = "OVERDUE",
}

export enum MergeReason {
  /** UPGRADE_APP - A V1 app that had been used by the source customer was upgraded to a V2 app. */
  UPGRADE_APP = "UPGRADE_APP",
  /** EMAIL_CLAIM - The target customer claimed an email address owned by the source customer. */
  EMAIL_CLAIM = "EMAIL_CLAIM",
  /**
   * SMS_AND_PASSCODE - The target customer claimed an SMS number owned by the source customer, and subsequently
   * verified ownership of the linked card by supplying its passcode.
   *
   * @deprecated
   */
  SMS_AND_PASSCODE = "SMS_AND_PASSCODE",
  /**
   * CARD_LINK_AND_NAME - The target customer matched the source customer on a successful card link and name.
   *
   * @deprecated
   */
  CARD_LINK_AND_NAME = "CARD_LINK_AND_NAME",
  /**
   * CARD_LINK_AND_SMS - The target customer matched the source customer on a successful card link and SMS number.
   *
   * @deprecated
   */
  CARD_LINK_AND_SMS = "CARD_LINK_AND_SMS",
  /**
   * CARD_LINK_AND_IDENTITY - The target customer matched the source customer on a successful card link and either
   * name or SMS number.
   *
   * @deprecated
   */
  CARD_LINK_AND_IDENTITY = "CARD_LINK_AND_IDENTITY",
  /**
   * PAYMENT_TOKEN - A payment was sent to an email address not yet belonging to a customer, which created a
   * dummy customer. The target customer claimed this payment and merged with the dummy customer.
   */
  PAYMENT_TOKEN = "PAYMENT_TOKEN",
  /**
   * SMS_CLAIM - A payment was sent to an SMS number not yet belonging to a customer, which created a
   * dummy customer. The target customer claimed the number and merged with the dummy customer.
   */
  SMS_CLAIM = "SMS_CLAIM",
  /** MANUAL - The merge was done manually, e.g. through an admin endpoint. */
  MANUAL = "MANUAL",
  /**
   * CARD_LINK_AND_EMAIL - The target customer matched the source customer on a successful card link and email address.
   *
   * @deprecated
   */
  CARD_LINK_AND_EMAIL = "CARD_LINK_AND_EMAIL",
  /**
   * EMAIL_AND_PASSCODE - The target customer claimed an email address owned by the source customer, and subsequently
   * verified ownership of the linked card by supplying its passcode.
   *
   * @deprecated
   */
  EMAIL_AND_PASSCODE = "EMAIL_AND_PASSCODE",
  /**
   * INSTRUMENT - The source customer linked an instrument in onboarding that is already linked to an existing
   * customer. We suggest a merge as an onboarding blocker.
   */
  INSTRUMENT = "INSTRUMENT",
  /**
   * TOKEN_FUTURIZER - Around April 2019 we started futurizing all legacy tokens (i.e. 27- and 10-character tokens) to
   * 11-character tokens. More details here: https://go.sqprod.co/cashFuturizedTokens
   *
   * TODO(svanderveen): Deprecate when TokenFuturizer is deleted.
   */
  TOKEN_FUTURIZER = "TOKEN_FUTURIZER",
  /** ACCOUNT_RECOVERY - The initiator customer went through an account recovery flow and verified the target's assets. */
  ACCOUNT_RECOVERY = "ACCOUNT_RECOVERY",
  /**
   * TOKEN_FUTURIZER_TOKEN_SWAP - A version of {@link #TOKEN_FUTURIZER} that uses the "token swap" strategy as a way to
   * potentially reduce load on the servers.
   *
   * TODO(svanderveen): Deprecate when TokenFuturizer is deleted.
   */
  TOKEN_FUTURIZER_TOKEN_SWAP = "TOKEN_FUTURIZER_TOKEN_SWAP",
  /** ACCOUNT_RECOVERY_V2 - The initiator customer went through the new account recovery flow. */
  ACCOUNT_RECOVERY_V2 = "ACCOUNT_RECOVERY_V2",
}

export enum MergeResolution {
  NOT_ELIGIBLE = "NOT_ELIGIBLE",
  MERGED_IMMEDIATELY = "MERGED_IMMEDIATELY",
  MERGED_USING_PASSCODE = "MERGED_USING_PASSCODE",
  CONFIRMED = "CONFIRMED",
  DECLINED = "DECLINED",
  /**
   * NAME_CONFIRMED - Deprecated - no new entries will use this.
   *
   * @deprecated
   */
  NAME_CONFIRMED = "NAME_CONFIRMED",
  /**
   * NAME_MISMATCHED - Deprecated - no new entries will use this.
   *
   * @deprecated
   */
  NAME_MISMATCHED = "NAME_MISMATCHED",
  /**
   * POSSIBLE_ATO - Deprecated - no new entries will use this.
   *
   * @deprecated
   */
  POSSIBLE_ATO = "POSSIBLE_ATO",
  /**
   * CARD_MATCHED - Deprecated - no new entries will use this.
   *
   * @deprecated
   */
  CARD_MATCHED = "CARD_MATCHED",
  TOO_MANY_ALIAS_VERIFICATION_ATTEMPTS = "TOO_MANY_ALIAS_VERIFICATION_ATTEMPTS",
  EXTRA_VERIFICATION_COMPLETE = "EXTRA_VERIFICATION_COMPLETE",
  EXTRA_VERIFICATION_FAILED = "EXTRA_VERIFICATION_FAILED",
  EXTRA_VERIFICATION_SKIPPED = "EXTRA_VERIFICATION_SKIPPED",
  /**
   * LOGGED_IN_WITHOUT_EXTRA_VERIFICATION - Like MERGED_IMMEDIATELY, but with a minimal asset transfer to login, rather than a full
   * customer merge.
   */
  LOGGED_IN_WITHOUT_EXTRA_VERIFICATION = "LOGGED_IN_WITHOUT_EXTRA_VERIFICATION",
  /**
   * LOGGED_IN_WITH_EXTRA_VERIFICATION - Like EXTRA_VERIFICATION_COMPLETE, but with a minimal asset transfer to login, rather than a
   * full customer merge.
   */
  LOGGED_IN_WITH_EXTRA_VERIFICATION = "LOGGED_IN_WITH_EXTRA_VERIFICATION",
  /** TRANSFERRED_ALIAS - Similar to what happens with EXTRA_VERIFICATION_SKIPPED for profile alias claim. */
  TRANSFERRED_ALIAS = "TRANSFERRED_ALIAS",
  /**
   * LINKED_IMMEDIATELY - Similar to MERGED_IMMEDIATELY, but with a minimal asset transfer to link an external
   * integration account, rather than a full customer merge.
   */
  LINKED_IMMEDIATELY = "LINKED_IMMEDIATELY",
  /**
   * CLAIMED_IMMEDIATELY - Similar to MERGED_IMMEDIATELY, but with a minimal asset transfer to claim a payment,
   * rather than a full customer merge.
   */
  CLAIMED_IMMEDIATELY = "CLAIMED_IMMEDIATELY",
  /** MANUALLY_LOGGED_IN - Indicates that a customer was manually logged in (e.g., via an advocate). */
  MANUALLY_LOGGED_IN = "MANUALLY_LOGGED_IN",
  /**
   * ACCESS_GRANTED - Indicates that a customer was granted access to an existing customer via the transfer of
   * profile aliases.
   */
  ACCESS_GRANTED = "ACCESS_GRANTED",
  /** RATE_LIMITED - The MergeAttempt is rate limited, this happens only when TransferMethod = LOGIN. */
  RATE_LIMITED = "RATE_LIMITED",
}

export enum CryptocurrencyTransferFailureReason {
  GENERAL_FAILURE = "GENERAL_FAILURE",
  EXCEEDED_DAILY_LIMIT = "EXCEEDED_DAILY_LIMIT",
  EXCEEDED_WEEKLY_LIMIT = "EXCEEDED_WEEKLY_LIMIT",
  AMOUNT_BELOW_MINIMUM = "AMOUNT_BELOW_MINIMUM",
  INVALID_ADDRESS = "INVALID_ADDRESS",
  /** CONCURRENT_MODIFICATION - Indicates a prior withdrawal was in progress */
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
  /** EXCEEDED_DAILY_COUNT - The customer has reached their maximum number of withdrawals per 1 day */
  EXCEEDED_DAILY_COUNT = "EXCEEDED_DAILY_COUNT",
}

export interface PaymentSourceSummary {
  fidelius_token?: string;
  /**
   * Customers who have received money from this source
   *  NOTE: only Summary fields will be populated.
   */
  recipient?: Customer[];
  /**
   * Customers who have successfully linked this source
   * NOTE: only Summary fields will be populated.
   */
  sender?: Customer[];
  /**
   * Customers who have failed to link this source
   * NOTE: only summary fields will be populated.
   */
  failed?: Customer[];
}

/**
 * Represents the decision for verification attempt of an address. VERIFIED means we have evidence
 * from reliable source (e.g. government id, 3rd party vendors etc.) that the address belongs to the
 * customer.
 */
export interface AddressVerification {
  address_id?: string;
  address?: GlobalAddress;
  decision?: AddressVerificationDecision;
  /** Corresponds to address_line_1, address_line_2, ..., address_line_5 in GlobalAddress. */
  street_address_match_result?: AddressVerificationMatchResult;
  /** Corresponds to postal_code in GlobalAddress. */
  postal_code_match_result?: AddressVerificationMatchResult;
  /** Corresponds to locality, sublocality, sublocality_1, ..., sublocality_5 in GlobalAddress. */
  city_match_result?: AddressVerificationMatchResult;
  /** Corresponds to administrative_district_level_1 in GlobalAddress. */
  state_match_result?: AddressVerificationMatchResult;
  verified_address_source?: AddressVerificationSource;
  address_token?: string;
}

export enum AddressVerificationDecision {
  VERIFIED = "VERIFIED",
  FAILED_TO_VERIFY = "FAILED_TO_VERIFY",
  RESCINDED = "RESCINDED",
}

export enum AddressVerificationMatchResult {
  MATCHED = "MATCHED",
  NOT_MATCHED = "NOT_MATCHED",
  /**
   * NOT_APPLICABLE - This value is used when a field isn't applicable for a region. e.g. state_match_result is not
   * applicable for UK as they don't have states.
   */
  NOT_APPLICABLE = "NOT_APPLICABLE",
}

export enum AddressVerificationSource {
  GOVERNMENT_ID = "GOVERNMENT_ID",
  IDOLOGY = "IDOLOGY",
  TLO = "TLO",
  ID_MATCH = "ID_MATCH",
  LEXIS = "LEXIS",
  CARD_ACTIVATION = "CARD_ACTIVATION",
  ALLOY = "ALLOY",
}

/**
 * Represents a Cash transfer between customer instruments. https://goo.gl/lo1d4s
 * When adding a new state for transfers, this state must be added to the enum list in
 * the transfer_state_transition table structure.
 */
export interface Transfer {
  token?: string;
  client_id?: string;
  external_id?: string;
  customer_token?: string;
  customer?: Customer;
  /** Deprecated. Prefer source_instrument. */
  type?: TransferType;
  /** @deprecated */
  source?: Instrument;
  /**
   * Deprecated. Prefer target_instrument.
   *
   * @deprecated
   */
  target?: Instrument;
  source_instrument?: InstrumentLink;
  target_instrument?: InstrumentLink;
  amount?: Money;
  pull_amount?: Money;
  push_amount?: Money;
  deposit_preference?: DepositPreference;
  state?: TransferState;
  version?: number;
  failure_reason?: TransferFailureReason;
  /** All dates are in milliseconds since 1/1/1970 UTC. */
  created_at?: number;
  /**
   * Time when the client attempted to make a request in offline mode. Provided in the RetryContext
   * of retried payment requests.
   */
  client_created_at?: number;
  updated_at?: number;
  /** Transfer was created and is waiting on customer. */
  waiting_on_customer_at?: number;
  /** The PULL was initiated at. */
  pull_started_at?: number;
  /** The PULL completed at. */
  pulled_at?: number;
  /** Risk approved/declined at. */
  risk_decision_at?: number;
  /** When the push was initiated. */
  push_started_at?: number;
  /** The PUSH was successfully sent at. */
  pushed_at?: number;
  /** The PUSH asynchronously completed at. */
  completed_at?: number;
  /** Reversing started at. */
  reversing_started_at?: number;
  /** The transfer was reversed at. */
  reversed_at?: number;
  /** The transfer failed at. */
  failed_at?: number;
  pulls?: PaymentPull[];
  pushes?: PaymentPush[];
  refund_pulls?: PaymentPull[];
  creating_browser_interaction?: BrowserInteraction;
  requires_identity_verification?: boolean;
  requires_passcode_confirmation?: boolean;
  passcode_confirmed_at?: number;
  estimated_arrival_at?: number;
  forced_instant_deposit_with_fee_at?: number;
  signals_context?: SignalsContext;
  oct_delayed_until?: number;
}

export enum TransferType {
  CASH_IN = "CASH_IN",
  CASH_OUT = "CASH_OUT",
  CRYPTOCURRENCY_BUY = "CRYPTOCURRENCY_BUY",
  CRYPTOCURRENCY_SELL = "CRYPTOCURRENCY_SELL",
  CURRENCY_EXCHANGE = "CURRENCY_EXCHANGE",
  EQUITY_BUY = "EQUITY_BUY",
  EQUITY_SELL = "EQUITY_SELL",
  /** BANK_TRANSFER - Captures Direct Deposit and external bank transfers */
  BANK_TRANSFER = "BANK_TRANSFER",
}

export enum TransferState {
  WAITING_ON_CUSTOMER = "WAITING_ON_CUSTOMER",
  WAITING_ON_LIMITS = "WAITING_ON_LIMITS",
  PULLING = "PULLING",
  WAITING_ON_PULL = "WAITING_ON_PULL",
  WAITING_ON_RISK = "WAITING_ON_RISK",
  WAITING_TO_PUSH = "WAITING_TO_PUSH",
  PUSHING = "PUSHING",
  WAITING_ON_PUSH = "WAITING_ON_PUSH",
  COMPLETE = "COMPLETE",
  /**
   * REVERSING - Prefer REVERSING_PUSH/WAITING_ON_PUSH_REVERSAL/REVERSING_PULL flow.
   *
   * @deprecated
   */
  REVERSING = "REVERSING",
  /** @deprecated */
  WAITING_ON_REVERSE = "WAITING_ON_REVERSE",
  REVERSING_PUSH = "REVERSING_PUSH",
  WAITING_ON_PUSH_REVERSAL = "WAITING_ON_PUSH_REVERSAL",
  REVERSING_PULL = "REVERSING_PULL",
  FAILED = "FAILED",
  RETREATING = "RETREATING",
  WAITING_ON_INSTRUMENT_SELECTION = "WAITING_ON_INSTRUMENT_SELECTION",
  WAITING_ON_MONEY_TRANSFER_TO_PROCESS = "WAITING_ON_MONEY_TRANSFER_TO_PROCESS",
  WAITING_ON_MONEY_TRANSFER_TO_SETTLE = "WAITING_ON_MONEY_TRANSFER_TO_SETTLE",
}

export enum TransferFailureReason {
  /** UNSUPPORTED_SOURCE_INSTRUMENT - The provided source instrument is unsupported. */
  UNSUPPORTED_SOURCE_INSTRUMENT = "UNSUPPORTED_SOURCE_INSTRUMENT",
  /** UNSUPPORTED_TARGET_INSTRUMENT - The provided target instrument is unsupported. */
  UNSUPPORTED_TARGET_INSTRUMENT = "UNSUPPORTED_TARGET_INSTRUMENT",
  /** UNSUPPORTED_INSTRUMENT_COMBINATION - The provided combination of instruments is unsupported. */
  UNSUPPORTED_INSTRUMENT_COMBINATION = "UNSUPPORTED_INSTRUMENT_COMBINATION",
  /** FAILED_PASSCODE_CHECK - The provided passcode failed to authenticate. */
  FAILED_PASSCODE_CHECK = "FAILED_PASSCODE_CHECK",
  /** FAILED_PASSCODE_CHECK_TOO_MANY_ATTEMPTS - The provided passcode failed to authenticate and there are no more attempts available. */
  FAILED_PASSCODE_CHECK_TOO_MANY_ATTEMPTS = "FAILED_PASSCODE_CHECK_TOO_MANY_ATTEMPTS",
  /** FAILED_PASSCODE_CHECK_CARD_BLOCKED - The provided passcode failed to authenticate because the card is blocked. */
  FAILED_PASSCODE_CHECK_CARD_BLOCKED = "FAILED_PASSCODE_CHECK_CARD_BLOCKED",
  /** FAILED_PASSCODE_TOKEN_CHECK - The provided passcode token failed to authenticate. */
  FAILED_PASSCODE_TOKEN_CHECK = "FAILED_PASSCODE_TOKEN_CHECK",
  /** IDV_REQUIRED - Customer must complete identity verification to transfer the requested amount. */
  IDV_REQUIRED = "IDV_REQUIRED",
  /** FAILED_IDV_TOO_MANY_ATTEMPS - Failed to verify idv and there are no more attempts. */
  FAILED_IDV_TOO_MANY_ATTEMPS = "FAILED_IDV_TOO_MANY_ATTEMPS",
  /** FAILED_SMS_CHECK - Failed to add sms required for the transfer. */
  FAILED_SMS_CHECK = "FAILED_SMS_CHECK",
  /** FAILED_EMAIL_CHECK - Failed to add email required for the transfer. */
  FAILED_EMAIL_CHECK = "FAILED_EMAIL_CHECK",
  /** PULL_FAILED - We could not pull the funds from the customer's bank account or debit card. */
  PULL_FAILED = "PULL_FAILED",
  /** PUSH_FAILED - We could not push the funds to the customer's bank account or debit card. */
  PUSH_FAILED = "PUSH_FAILED",
  /** AMOUNT_BELOW_MINIMUM - The transfer amount cannot be less than $1. */
  AMOUNT_BELOW_MINIMUM = "AMOUNT_BELOW_MINIMUM",
  /**
   * CONCURRENT_MODIFICATION - The transfer could not be processed due to a concurrent modification, such as the request
   * did not contain a passcode when one was required.
   */
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
  /** EXPIRED_AS_DRAFT - The transfer never made it past WAITING_ON_CUSTOMER. */
  EXPIRED_AS_DRAFT = "EXPIRED_AS_DRAFT",
  /** BALANCE_LIMIT_EXCEEDED - The transfer would cause the customer's balance to exceed the maximum allowed. */
  BALANCE_LIMIT_EXCEEDED = "BALANCE_LIMIT_EXCEEDED",
  /** AMOUNT_ABOVE_BALANCE - The transfer amount exceeds the customer's available balance. */
  AMOUNT_ABOVE_BALANCE = "AMOUNT_ABOVE_BALANCE",
  /** SINGLE_TRANSACTION_AMOUNT_LIMIT_EXCEEDED - The transfer amount would exceed the instrument's limit for a single transaction. */
  SINGLE_TRANSACTION_AMOUNT_LIMIT_EXCEEDED = "SINGLE_TRANSACTION_AMOUNT_LIMIT_EXCEEDED",
  /** AMOUNT_LIMIT_EXCEEDED - The transfer amount would exceed the instrument's weekly limit. */
  AMOUNT_LIMIT_EXCEEDED = "AMOUNT_LIMIT_EXCEEDED",
  /** DAILY_COUNT_LIMIT_EXCEEDED - The transfer exceeds the number of daily allowed transfers. */
  DAILY_COUNT_LIMIT_EXCEEDED = "DAILY_COUNT_LIMIT_EXCEEDED",
  /** AMOUNT_LIMIT_EXCEEDED_FLORIDA - The transfer amount would exceed the Florida's transaction limit. */
  AMOUNT_LIMIT_EXCEEDED_FLORIDA = "AMOUNT_LIMIT_EXCEEDED_FLORIDA",
  /**
   * TRIANGLE_CASH_OUT - Triangle cash outs happen when a customer adds cash in their stored value from card A, then
   * immediately tries to cash out to card B. For a period of time, we restrict cash outs to just
   * card A.
   */
  TRIANGLE_CASH_OUT = "TRIANGLE_CASH_OUT",
  /**
   * MULTIPLE_CARD_CASH_IN - This is related to the triangle cash out above. We restrict the number of cards that
   * customers can use to cash into their balance to one. This allows us to enforce the no
   * triangle cash out property, without having to resolve a complex multi-card situation.
   */
  MULTIPLE_CARD_CASH_IN = "MULTIPLE_CARD_CASH_IN",
  /** CASH_IN_INSTRUMENT_BLACKLISTED - These two statuses mean that either the cash-in or cash-out instrument was denylisted. */
  CASH_IN_INSTRUMENT_BLACKLISTED = "CASH_IN_INSTRUMENT_BLACKLISTED",
  /**
   * CASH_OUT_INSTRUMENT_BLACKLISTED - As of Q2 2023, cash-outs should no longer be blocked due to a denylisted instrument.
   * Do not use this failure reason.
   *
   * @deprecated
   */
  CASH_OUT_INSTRUMENT_BLACKLISTED = "CASH_OUT_INSTRUMENT_BLACKLISTED",
  CASH_IN_INSTRUMENT_DENYLISTED = "CASH_IN_INSTRUMENT_DENYLISTED",
  /**
   * CASH_OUT_INSTRUMENT_DENYLISTED - As of Q2 2023, cash-outs should no longer be blocked due to a denylisted instrument.
   * Do not use this failure reason.
   *
   * @deprecated
   */
  CASH_OUT_INSTRUMENT_DENYLISTED = "CASH_OUT_INSTRUMENT_DENYLISTED",
  /** RATE_LIMITED - The customer cashed in too many times and was rate limited. */
  RATE_LIMITED = "RATE_LIMITED",
  /** AUTOMATICALLY_BLOCKED - Transfer blocked by risk. */
  AUTOMATICALLY_BLOCKED = "AUTOMATICALLY_BLOCKED",
  /**
   * CUSTOMER_NOT_ELIGIBLE_FOR_CASH_IN - The UI should prevent ineligible customers from seeing the Add Cash button. However, we
   * also check at validation to make sure.
   */
  CUSTOMER_NOT_ELIGIBLE_FOR_CASH_IN = "CUSTOMER_NOT_ELIGIBLE_FOR_CASH_IN",
  /** INVALID_SOURCE_INSTRUMENT - The provided source instrument is invalid. */
  INVALID_SOURCE_INSTRUMENT = "INVALID_SOURCE_INSTRUMENT",
  /** INVALID_TARGET_INSTRUMENT - The provided target instrument is invalid. */
  INVALID_TARGET_INSTRUMENT = "INVALID_TARGET_INSTRUMENT",
  /** ALMOST_EXPIRED_SOURCE_INSTRUMENT - The source instrument is almost expired. Prevent Cash In to avoid triangle issues. */
  ALMOST_EXPIRED_SOURCE_INSTRUMENT = "ALMOST_EXPIRED_SOURCE_INSTRUMENT",
  /** EXPIRED_SOURCE_INSTRUMENT - The provided source instrument is expired. */
  EXPIRED_SOURCE_INSTRUMENT = "EXPIRED_SOURCE_INSTRUMENT",
  /** EXPIRED_TARGET_INSTRUMENT - The provided target instrument is expired. */
  EXPIRED_TARGET_INSTRUMENT = "EXPIRED_TARGET_INSTRUMENT",
  /** CUSTOMER_CHARGEBACK - Chargeback received by customer */
  CUSTOMER_CHARGEBACK = "CUSTOMER_CHARGEBACK",
  /**
   * CUSTOMER_NAME_MISSING - The customer had no name, which would cause the pull or push to fail. At some point, this
   * should be handled by a blocker, but since it's an edge case that should only be possible in
   * the web, failing the transfer was the best approach for launch.
   */
  CUSTOMER_NAME_MISSING = "CUSTOMER_NAME_MISSING",
  /** CUSTOMER_BLACKLISTED - Customer was denylisted and cannot cash out at the moment. */
  CUSTOMER_BLACKLISTED = "CUSTOMER_BLACKLISTED",
  CUSTOMER_DENYLISTED = "CUSTOMER_DENYLISTED",
  /** CUSTOMER_SUSPENDED - Customer was suspended and cannot cash in at the moment. */
  CUSTOMER_SUSPENDED = "CUSTOMER_SUSPENDED",
  /** UNEXPECTED_ACCEPTED_FEE - Accepted fee sent by the client is not what the server expected. */
  UNEXPECTED_ACCEPTED_FEE = "UNEXPECTED_ACCEPTED_FEE",
  /** MANUALLY_REVERSED - Manually reversed by human. */
  MANUALLY_REVERSED = "MANUALLY_REVERSED",
  /** CLAWBACK - Clawed back due to negative funds. */
  CLAWBACK = "CLAWBACK",
  /** TOO_MANY_CUSTOMERS_CASHED_OUT_TO_INSTRUMENT - Too many customers cashing out to instrument. */
  TOO_MANY_CUSTOMERS_CASHED_OUT_TO_INSTRUMENT = "TOO_MANY_CUSTOMERS_CASHED_OUT_TO_INSTRUMENT",
  /**
   * CASH_IN_LIKELY_CASH_OUT - The customer received a payment and then cashed in the same amount. Likely a mistake.
   * Deprecating since this cashing in same amount can actually be a use case now.
   *
   * @deprecated
   */
  CASH_IN_LIKELY_CASH_OUT = "CASH_IN_LIKELY_CASH_OUT",
  /**
   * CUSTOMER_CANCELED - The customer manually canceled the transfer. This can happen for retried transfers that
   * go into the pending section of activity.
   */
  CUSTOMER_CANCELED = "CUSTOMER_CANCELED",
  /**
   * EXCEEDED_RETRY_EXPIRATION - An offline transfer was retried much later (configurable) than it was originally attempted
   * the app.
   */
  EXCEEDED_RETRY_EXPIRATION = "EXCEEDED_RETRY_EXPIRATION",
  /** RISK_EVALUATION_ERROR - There was an RPC error evaluating the transfer for risk, and it was failed as a precaution. */
  RISK_EVALUATION_ERROR = "RISK_EVALUATION_ERROR",
  /** EXCHANGE_MARKET_UNAVAILABLE - Exchange market was unavailable. Try again later. */
  EXCHANGE_MARKET_UNAVAILABLE = "EXCHANGE_MARKET_UNAVAILABLE",
  /** EXCHANGE_INVALID_CONTRACT_TOKEN - The contract token referring to the exchange is invalid. */
  EXCHANGE_INVALID_CONTRACT_TOKEN = "EXCHANGE_INVALID_CONTRACT_TOKEN",
  /** EXCHANGE_EXPIRED_CONTRACT_TOKEN - The contract token referring to the exchange has expired. */
  EXCHANGE_EXPIRED_CONTRACT_TOKEN = "EXCHANGE_EXPIRED_CONTRACT_TOKEN",
  /** EXCHANGE_DISABLED - Exchange market has been disabled. */
  EXCHANGE_DISABLED = "EXCHANGE_DISABLED",
  /** EXCHANGE_DISABLED_BY_DENYLIST - Customer cannot complete exchange due to bitcoin denylist. */
  EXCHANGE_DISABLED_BY_DENYLIST = "EXCHANGE_DISABLED_BY_DENYLIST",
  /** EXCHANGE_DISABLED_BY_SUSPENSION - Customer cannot complete exchange due to suspension. */
  EXCHANGE_DISABLED_BY_SUSPENSION = "EXCHANGE_DISABLED_BY_SUSPENSION",
  /** CURRENCY_EXCHANGE_CASH_IN_FAILED - Cash in transfer failed during currency purchase. */
  CURRENCY_EXCHANGE_CASH_IN_FAILED = "CURRENCY_EXCHANGE_CASH_IN_FAILED",
  /** EQUITY_EXCHANGE_FAILED - Generic failure when customer cannot complete equity exchange. */
  EQUITY_EXCHANGE_FAILED = "EQUITY_EXCHANGE_FAILED",
  /** INVESTMENT_CASH_IN_FAILED - Cash in transfer failed during investment purchase. */
  INVESTMENT_CASH_IN_FAILED = "INVESTMENT_CASH_IN_FAILED",
  /** INVESTMENT_ENTITY_NOT_TRADABLE - Investment entity currently disabled for trade. */
  INVESTMENT_ENTITY_NOT_TRADABLE = "INVESTMENT_ENTITY_NOT_TRADABLE",
  /** PATTERN_DAY_TRADING_INVESTMENT - Trade is blocked because the customer reached his day-trading limit. */
  PATTERN_DAY_TRADING_INVESTMENT = "PATTERN_DAY_TRADING_INVESTMENT",
  /**
   * SCHEDULED_ORDER_ALREADY_IN_FLIGHT - Trade is blocked because there can only be one investment trade
   * per customer, per investment entity in-flight at a time.
   */
  SCHEDULED_ORDER_ALREADY_IN_FLIGHT = "SCHEDULED_ORDER_ALREADY_IN_FLIGHT",
  /** REFUNDING_INSTRUMENT_VERIFICATION - Amount was refunded as part of instrument verification. */
  REFUNDING_INSTRUMENT_VERIFICATION = "REFUNDING_INSTRUMENT_VERIFICATION",
  /** PURCHASE_AMOUNT_LIMIT_EXCEEDED - Bitcoin purchase limit exceeded. */
  PURCHASE_AMOUNT_LIMIT_EXCEEDED = "PURCHASE_AMOUNT_LIMIT_EXCEEDED",
  /**
   * WAITING_BROKER_APPROVAL - Investing customers that go through dIDV needs to be approved by a licensed broker before
   * they can buy or sell investments.
   */
  WAITING_BROKER_APPROVAL = "WAITING_BROKER_APPROVAL",
  /** REJECTED_BY_BROKER - A licensed broker rejected customer dIDV. */
  REJECTED_BY_BROKER = "REJECTED_BY_BROKER",
  /** INVESTMENT_MARKET_CLOSED - Investing order was rejected because market closed before order could be executed. */
  INVESTMENT_MARKET_CLOSED = "INVESTMENT_MARKET_CLOSED",
  /** CONFIRMED_INVESTMENT_AFFILIATION - Customer confirmed affiliation with a brokerage firm or FINRA. */
  CONFIRMED_INVESTMENT_AFFILIATION = "CONFIRMED_INVESTMENT_AFFILIATION",
  /** FUNDED_PAYMENT_FAILED - Amount was refunded because the payment funded by the transfer failed. */
  FUNDED_PAYMENT_FAILED = "FUNDED_PAYMENT_FAILED",
  /**
   * ORDER_AWAITING_FULFILLMENT - Trade is blocked because there can only be one investment trade
   * per customer, per investment entity in-flight awaiting to be fulfilled at a time.
   */
  ORDER_AWAITING_FULFILLMENT = "ORDER_AWAITING_FULFILLMENT",
  /** INVESTMENT_MARKET_HALT - Investing order was rejected because a market halt. */
  INVESTMENT_MARKET_HALT = "INVESTMENT_MARKET_HALT",
  /** INVESTMENT_ENTITY_DELISTED - Investing order was rejected because the investment entity was delisted. */
  INVESTMENT_ENTITY_DELISTED = "INVESTMENT_ENTITY_DELISTED",
  /** SCHEDULED_ORDERS_DISABLED - Investing schedule orders are disabled. */
  SCHEDULED_ORDERS_DISABLED = "SCHEDULED_ORDERS_DISABLED",
  /**
   * INVESTMENT_CLOSE_POSITIONS_ONLY - Investing order was rejected for the given Investment Entity by clearing vendor due to capital
   * requirements. Only sells are allowed.
   */
  INVESTMENT_CLOSE_POSITIONS_ONLY = "INVESTMENT_CLOSE_POSITIONS_ONLY",
  /** NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS - Action not available for sponsored accounts */
  NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS = "NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS",
  /** NOT_AVAILABLE_FOR_PENDING_SPONSORED_ACCOUNTS - Action not available for pending sponsored accounts */
  NOT_AVAILABLE_FOR_PENDING_SPONSORED_ACCOUNTS = "NOT_AVAILABLE_FOR_PENDING_SPONSORED_ACCOUNTS",
  /** INVESTING_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS - Investing not available for sponsored accounts */
  INVESTING_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS = "INVESTING_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS",
  /** BITCOIN_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS - Bitcoin not available for sponsored accounts */
  BITCOIN_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS = "BITCOIN_NOT_AVAILABLE_FOR_SPONSORED_ACCOUNTS",
  /** CASH_OUT_INSTANT_BELOW_MINIMUM - Cash out instantly under minimum amount */
  CASH_OUT_INSTANT_BELOW_MINIMUM = "CASH_OUT_INSTANT_BELOW_MINIMUM",
  /** INVESTING_ORDER_QUANTITY_TOO_LARGE - Investing order quantity exceeds amount supported by provider */
  INVESTING_ORDER_QUANTITY_TOO_LARGE = "INVESTING_ORDER_QUANTITY_TOO_LARGE",
  /** NO_SOURCE_INSTRUMENT_SELECTED - Instrument selection failed */
  NO_SOURCE_INSTRUMENT_SELECTED = "NO_SOURCE_INSTRUMENT_SELECTED",
  /** INSTANT_ACH_DISABLED - Instant ACH disabled */
  INSTANT_ACH_DISABLED = "INSTANT_ACH_DISABLED",
  /** CUSTOMER_HAS_MONEY_MOVEMENT_BLOCK - Money movement block */
  CUSTOMER_HAS_MONEY_MOVEMENT_BLOCK = "CUSTOMER_HAS_MONEY_MOVEMENT_BLOCK",
}

/** Reasons that a refund of a transfer can fail. */
export enum TransferRefundFailureReason {
  /** BANK_DECLINED - The refunder's bank declined the refund pull. */
  BANK_DECLINED = "BANK_DECLINED",
  /** MANUALLY_FAILED - The refund was forced to fail manually for some other reason. */
  MANUALLY_FAILED = "MANUALLY_FAILED",
}

/** An email sent by Cash. */
export interface OutgoingEmail {
  customer_token?: string;
  /** Unix time in millis */
  created_at?: number;
  message_id?: string;
  payment_id?: string;
  subject?: string;
  template?: string;
  email_address?: string;
  /** Unix time in millis */
  gateway_delivered_at?: number;
  /** Unix time in millis */
  bounced_at?: number;
  bounce_type?: string;
  bounce_reason?: string;
}

export interface BinRule {
  bin?: string;
  tag?: string;
  direction?: Direction;
  policy?: string;
  bin_tags?: Tag[];
}

/**
 * A single constraint on selecting a money movement mechanism (AFT, SHAZAM, etc.) for a Bank
 * Identification Number (BIN). The rules change over time due to bugs & business decisions!
 */
export interface RoutingRule {
  bin?: string;
  direction?: Direction;
  mechanism?: PaymentMoneyMovingMechanism;
  policy?: RoutingRulePolicy;
  traffic_weight?: number;
  preference?: number;
  coding_category?: RoutingRuleCodingCategory;
  /** The reason this routing rule exists (may be null) */
  reason?: string;
  /** These are used only by BinMechanismPolicyFeed */
  id?: string;
  created_at?: number;
  updated_at?: number;
  /** Only used for Bin Router */
  rule_type?: RuleType;
  value?: number;
  tag?: string;
}

export enum RoutingRulePolicy {
  /** DEFAULT - May route over this mechanism, or not, depending on other constraints & rules. */
  DEFAULT = "DEFAULT",
  /** FORCE - Always route over the mechanism. */
  FORCE = "FORCE",
  /**
   * BLACKLIST - Deprecated - use DENYLIST instead
   * Never route over the mechanism.
   *
   * @deprecated
   */
  BLACKLIST = "BLACKLIST",
  /** DENYLIST - Never route over the mechanism. */
  DENYLIST = "DENYLIST",
}

export enum RoutingRuleCodingCategory {
  /** ALL - All Payment Coding Categories */
  ALL = "ALL",
  /** P2P - A regular P2P transaction */
  P2P = "P2P",
  /** MERCHANT_SETTLEMENT - A transaction used to settle a Square Register merchant. */
  MERCHANT_SETTLEMENT = "MERCHANT_SETTLEMENT",
  /** COMMERCIAL - Cash Pro pulls and pushes */
  COMMERCIAL = "COMMERCIAL",
}

/** Represents a Franklin customer node which contains a subset of Customer. */
export interface CustomerNode {
  /** This customer's Franklin customer token. */
  customer_token?: string;
  email_address?: string;
  /** Tags are out of order. Next tag = 5. */
  full_name?: string;
}

/** Represents a Franklin payment edge which contains a subset of Payment. */
export interface PaymentEdge {
  id?: string;
  sender_customer_token?: string;
  recipient_customer_token?: string;
  amount?: Money;
  state?: PaymentState;
  failure_reason?: PaymentFailureReason;
  created_at?: number;
  orientation?: Orientation;
  chargeback_at?: number;
}

export interface CustomerUpgradeInformation {
  customer_token?: string;
  government_id?: GovernmentId[];
}

export interface AppInstall {
  app_token?: string;
  platform?: Platform;
  major_version?: number;
  minor_version?: number;
  patch_version?: number;
  user_agent?: string;
  device_uuid?: string;
  v1_app_token?: string;
  /** Entity version for downstream write serialization. Not an "app" or device version. */
  version?: number;
}

export interface AppSession {
  app_install?: AppInstall;
  customer_token?: string;
  session_status?: AppSessionSessionStatus;
}

export enum AppSessionSessionStatus {
  /** ACTIVE - Customer was vended a session token that is currently active for this app. */
  ACTIVE = "ACTIVE",
  /** SOFT_SIGNED_OUT - Customer can get a session token for this app again without supplying a CVV. */
  SOFT_SIGNED_OUT = "SOFT_SIGNED_OUT",
  /**
   * HARD_SIGNED_OUT - Customer has signed into this app before but they will be forced to verify CVV if they
   * try to sign into this app again.
   */
  HARD_SIGNED_OUT = "HARD_SIGNED_OUT",
}

export interface CustomerChargeback {
  customer_token?: string;
  chargebacks_as_sender?: number;
  chargebacks_as_recipient?: number;
  chargebacks_as_sender_amount_cents?: number;
  chargebacks_as_recipient_amount_cents?: number;
  chargebacks_on_transfers?: number;
  chargebacks_on_transfers_amount_cents?: number;
}

export interface PlatformClient {
  id?: string;
  name?: string;
  display_name?: string;
  icon_url?: string;
  icon_name?: string;
  newId?: string;
}

export interface PlatformPaymentId {
  client_id?: string;
  payment_id?: string;
}

/** An invitation sent from an app. Includes info on the corresponding Cash alias, if it exists. */
export interface Invitation {
  id?: string;
  invited_at?: number;
  inviter_customer_token?: string;
  /** These can be null if this alias has not been seen in Cash yet. */
  alias?: Alias;
  alias_customer_token?: string;
  state?: InvitationState;
}

export enum InvitationState {
  ACTIVE = "ACTIVE",
  EXPIRED = "EXPIRED",
}

/** Summary of a customer's limit. */
export interface LimitSummary {
  limit_category?: LimitCategory;
  /**
   * Limit category. i.e. 'ACCOUNT SEND TO PERSONAL', 'RECEIVE BY CARD', etc.
   * Deprecated - use limit_category enum.
   *
   * @deprecated
   */
  category?: string;
  /**
   * The maximum allowed limit before the customer needs to upgrade or the payment fails.
   * This can be the dollar amount in cents or the count depending on the category.
   */
  limit?: number;
  /**
   * The current amount that has been sent or received in this category.
   * This can be the dollar amount in cents or the count depending on the category.
   */
  amount?: number;
  /** The running days that is applied to this limit category. */
  period_days?: number;
  /** The current total pending amount for this category. */
  pending_amount?: number;
  /** The current total paid out amount for this category. */
  paid_out_amount?: number;
}

/** Summary of a customer's particular Reward Code */
export interface RewardCode {
  code?: string;
  reward_program?: RewardProgram;
  state?: RewardCodeState;
  reward_program_name?: string;
}

export enum RewardCodeState {
  /** ENABLED - The referral code is enabled. */
  ENABLED = "ENABLED",
  /** DISABLED - The referral code is disabled and should not be used. */
  DISABLED = "DISABLED",
}

/**
 * A referral is created when a customer has joined after an invitation, or when they refer
 * themselves via a Try-It-Now payment. Either of the parties may be awarded a payment for the
 * referral.
 */
export interface Referral {
  id?: string;
  created_at?: number;
  referral_type?: ReferralType;
  referrer_customer_token?: string;
  referee_customer_token?: string;
  /** Payment IDs for referrer bounty and welcome bonus, if created. */
  referrer_bonus_payment_id?: string;
  referee_bonus_payment_id?: string;
  /**
   * For customers signing up with reward codes, reward payments are provided instead of welcome
   * bonuses. The maximum number of reward payments is specified in the limit.
   */
  reward_payment_limit?: number;
  reward_payments?: RewardPayment[];
  /** If the referral is being awarded based on an invitation, this will be populated. */
  invitation_id?: string;
  state?: ReferralState;
  failure_reason?: ReferralFailureReason;
  reward_program?: RewardProgram;
  reward_program_name?: string;
}

export enum ReferralFailureReason {
  CARD_ALREADY_USED = "CARD_ALREADY_USED",
  BANK_ACCOUNT_ALREADY_USED = "BANK_ACCOUNT_ALREADY_USED",
  ALREADY_GOT_REWARDS = "ALREADY_GOT_REWARDS",
  ALREADY_GOT_WELCOME_BONUS = "ALREADY_GOT_WELCOME_BONUS",
  FIDELIUS_TOKEN_ALREADY_USED = "FIDELIUS_TOKEN_ALREADY_USED",
  UNVERIFIED_DEVICE = "UNVERIFIED_DEVICE",
  FAILED_ON_MERGE = "FAILED_ON_MERGE",
  REFERRER_IS_SPONSOR = "REFERRER_IS_SPONSOR",
}

export enum ReferralState {
  /** PENDING - The referral is pending or expired depending on creation date. */
  PENDING = "PENDING",
  /** FAILED - The referral has failed and has a failure reason. */
  FAILED = "FAILED",
  /** COMPLETED - All bonus rewards for the invited customer have been been awarded. */
  COMPLETED = "COMPLETED",
  /** EXPIRED - The referral expired and rewards can no longer be claimed. */
  EXPIRED = "EXPIRED",
}

/**
 * For customers who signed up using a reward code, a reward payment record is created when they
 * are sent a reward payment for making a qualifying payment (e.g. sending cash in the app).
 */
export interface RewardPayment {
  reward_payment_id?: string;
  source_payment_id?: string;
  referral_id?: string;
}

/** Encapsulates the PII data collected for equity compliance. */
export interface EquityCompliancePiiData {
  employer_name?: string;
}

/** Encapsulates the PII data displayed to customers on their w9 form they are verifiying. */
export interface W9SnapshotPiiData {
  legal_name?: string;
  address?: GlobalAddress;
  accepted_terms_at?: number;
  business?: string;
  classification?: string;
  exemptions?: string;
  accounts?: string;
  ssn_token?: string;
  acknowledgement_blocker_terms_hash?: string;
  /** DriveWealth requires first name and last name separately. * */
  first_name?: string;
  last_name?: string;
}

/**
 * Encapsulates data for IDV verifications with government ID that gets serialized to the database.
 * For example, the failure reason if this verification was manually revoked.
 */
export interface GovernmentIdVerificationData {
  /** The failure reason if this verification was manually revoked. */
  failure_reason?: IdvSummaryFailureReason;
}

export interface AppMessageTemplateInfo {
  id?: string;
  name?: string;
  app_message_data?: AppMessageData;
}

export interface AppMessageCriteriaInfo {
  id?: string;
  name?: string;
  criteria?: string;
}

/** Describes a single AppMessage in a GetAppMessageInfoResponse. */
export interface AppMessageInfo {
  plan_id?: string;
  template?: AppMessageTemplateInfo;
  criteria?: AppMessageCriteriaInfo;
  total_notified?: number;
  total_viewed?: number;
  total_acted?: number;
  /** A map from action_identifier to count of registered actions received. */
  action_counts?: AppMessageInfoActionCountEntry[];
}

export interface AppMessageInfoActionCountEntry {
  action_identifier?: string;
  count?: number;
}

export interface PasscodeReset {
  id?: string;
  created_at?: number;
  flow_token?: string;
  alias?: Alias;
  alias_verification_attempts?: number;
  alias_verified_at?: number;
  /** @deprecated */
  ssn_idv_verification_id?: string;
  ssn_idv_verification_token?: string;
  ssn_verification_attempts?: number;
  ssn_verified_at?: number;
  instrument_link_id?: string;
  instrument_verification_attempts?: number;
  instrument_passcode_verified_at?: number;
  additional_alias?: Alias;
  additional_alias_verification_attempts?: number;
  additional_alias_verified_at?: number;
  manual_approval_required?: boolean;
  manually_approved_at?: number;
  passcode_reset_at?: number;
  failed_at?: number;
  failure_reason?: PasscodeResetFailureReason;
}

export enum PasscodeResetFailureReason {
  EXCEEDED_ALIAS_VERIFICATION_ATTEMPTS = "EXCEEDED_ALIAS_VERIFICATION_ATTEMPTS",
  COULD_NOT_SEND_VERIFICATION_CODE = "COULD_NOT_SEND_VERIFICATION_CODE",
  ALIAS_VERIFICATION_CODE_EXPIRED = "ALIAS_VERIFICATION_CODE_EXPIRED",
  EXCEEDED_CARD_VERIFICATION_ATTEMPTS = "EXCEEDED_CARD_VERIFICATION_ATTEMPTS",
  CARD_BLOCKED = "CARD_BLOCKED",
  EXCEEDED_SSN_VERIFICATION_ATTEMPTS = "EXCEEDED_SSN_VERIFICATION_ATTEMPTS",
  MANUALLY_FAILED = "MANUALLY_FAILED",
}

/**
 * Encapsulates a list of events for a specific Banklin transaction, such as a payment made by
 * an virtual card, or a direct deposit of a paycheck into a stored balance.
 */
export interface BankingTransactionData {
  events?: TransactionEvent[];
  /**
   * Deprecated: This information is tracked in Banklin's DbTransactionEvent.
   * Keeping around to be able to read historical data in Franklin.
   *
   * @deprecated
   */
  missingRewardReport?: MissingRewardReport;
}

/** Encapsulates the merchant information as received from banklin */
export interface MerchantData {
  merchant?: Merchant;
}

/** Summarizes a banking transaction for internal display. */
export interface BankingTransaction {
  events?: TransactionEvent[];
  /** The transaction's token. */
  token?: string;
  /** The token of the customer that made this transaction. */
  customer_token?: string;
  /** The token of the issued card that was used in this transaction. */
  instrument_token?: string;
  /**
   * The amount to display for this transaction. This is settled amount (if settled) or the
   * authorized amount otherwise.
   */
  amount?: Money;
  /** The date that the first event of the transaction occurred at. */
  display_date?: number;
  /** The state of the transaction, such as AUTHORIZED, SETTLED, DECLINED, etc. */
  state?: string;
  /** The transaction's description, which is currently always the merchant's name. */
  description?: string;
  /** A customer-visible reference code for tracking this transaction. */
  reference?: string;
  /** The reason code for the transaction. Such as APPLE_PAY_PURCHASE. */
  reason_code?: TransactionReasonCode;
  merchant_token?: string;
  last_four?: string;
}

/** Similar to the API IssuedCard but with extra information for regulator. */
export interface IssuedCard {
  /** A unique token used to identify this card on our system */
  token?: string;
  fidelius_token?: string;
  /** The card bin (first 6 digits of the pan). */
  bin?: string;
  /** The last four digits of the pan. */
  last_four?: string;
  /** An expiration date in YYMM format. */
  expiration?: Expiration;
  /** The type of payment instrument, such as Visa or MasterCard. */
  brand?: InstrumentType;
  /** When false, the card will always be declined if attempted to be used for a payment. */
  enabled?: boolean;
  /** True if the card is a virtual card. False if the card is a physical card. */
  virtual?: boolean;
  /** True if the card has been activated. */
  activated?: boolean;
  /**
   * Customers can have more than one issued card (virtual, physical, etc.)
   * This represents whether the card is the primary card or not.
   */
  primary?: boolean;
  /** True if the card is an emergency card. */
  emergency?: boolean;
  accepted_terms_at?: number;
}

/** Information for regulator pertaining to physical cards. */
export interface PhysicalCard {
  /** A unique token used to identify this card on our system */
  customization_token?: string;
  /** The last four digits of the PAN for the issued card. */
  last_four?: string;
  name_on_card?: string;
  address?: GlobalAddress;
  customization_review_state?: PhysicalCardCustomizationReviewState;
  customization_decline_reason?: CustomizationDeclineReason;
  created_at?: number;
  review_requested_at?: number;
  card_requested_at?: number;
  card_activated_at?: number;
  card_unlinked_at?: number;
  reported_lost_at?: number;
  is_active_card?: boolean;
  amendment_needed?: AmendCustomizationReason;
  manual_intervention_reason?: ManualInterventionReason;
  is_multi_card?: boolean;
  card_color?: string;
  card_price?: Money;
  shipping_method?: string;
  tracking_number?: string;
  creating_browser_interaction?: BrowserInteraction;
}

export enum PhysicalCardCustomizationReviewState {
  /** REVIEW_PENDING - The customization has been submitted for review but the decision has not been returned. */
  REVIEW_PENDING = "REVIEW_PENDING",
  /** CUSTOMIZATION_DENIED - The customization is not acceptable. */
  CUSTOMIZATION_DENIED = "CUSTOMIZATION_DENIED",
  /** CUSTOMIZATION_APPROVED - The customization is acceptable. */
  CUSTOMIZATION_APPROVED = "CUSTOMIZATION_APPROVED",
  /** MANUAL_INTERVENTION - Something went wrong during review, we should take a closer look. */
  MANUAL_INTERVENTION = "MANUAL_INTERVENTION",
  /** CUSTOMIZATION_AUTO_APPROVED - This customization is acceptable and was automatically approved. */
  CUSTOMIZATION_AUTO_APPROVED = "CUSTOMIZATION_AUTO_APPROVED",
}

/** Information on issued instruments for internal display. */
export interface IssuedInstrument {
  /** A unique token used to identify this card on our system */
  token?: string;
  fidelius_token?: string;
  /** The card bin (first 6 digits of the pan). */
  bin?: string;
  /** The last four digits of the pan. */
  last_four?: string;
  /** When false, the card will always be declined if attempted to be used for a payment. */
  enabled?: boolean;
  /** True if the card is a virtual card. False if the card is a physical card. */
  virtual?: boolean;
  /** True if the card has been activated. */
  activated?: boolean;
  /** True if the card has been suspended. */
  suspended?: boolean;
  /**
   * Customers can have more than one issued card (virtual, physical, etc.)
   * This represents whether the card is the primary card or not.
   */
  primary?: boolean;
  created_at?: number;
  issued_at?: number;
  deactivated_at?: number;
  card_issuer?: IssuedInstrumentCardIssuer;
  stored_value_token?: string;
  emergency?: boolean;
}

export enum IssuedInstrumentCardIssuer {
  MARQETA = "MARQETA",
  SHAZAM = "SHAZAM",
  VISA_DPS = "VISA_DPS",
}

/**
 * Collection of the information we need to customize a physical card for the customer (e.g.
 * their signature, legal name). Primary use to retrieve the customization so it can be reviewed.
 */
export interface CardCustomization {
  customer_token?: string;
  customization_token?: string;
  name_on_card?: string;
  image_trunk_token?: string;
  mailing_address?: GlobalAddress;
  ip_address?: string;
  created_at?: number;
}

/**
 * One role's view of a payment. This is currently limited to data not already covered by the
 * payment, though that won't necessarily always be the case.
 */
export interface Movement {
  movement_id?: string;
  payment_id?: string;
  role?: Role;
  /** Lease stuff. */
  lease_expires_at?: number;
  lease_holder?: string;
  /** Twin lock stuff. */
  lock_version?: number;
  twin_stale_at?: number;
  lock_state?: string;
}

/** The movement state machine currently maps payment states by role. */
export enum MovementMovementState {
  VALIDATING = "VALIDATING",
  SCHEDULED = "SCHEDULED",
  WAITING_ON_COUNTERPARTY = "WAITING_ON_COUNTERPARTY",
  WAITING_ON_CUSTOMER = "WAITING_ON_CUSTOMER",
  WAITING_ON_SQUARE = "WAITING_ON_SQUARE",
  MOVING_MONEY = "MOVING_MONEY",
  COMPLETE = "COMPLETE",
  REVERSING = "REVERSING",
  WAITING_FOR_REVERSE = "WAITING_FOR_REVERSE",
  /** FAILED - Tags are out of order. Next tag = 11. */
  FAILED = "FAILED",
}

/**
 * Determine how to handle blockers for a payment. Phases are inclusive of all previous phases.
 * PHASE_{N+1} does at least everything PHASE_N does.
 */
export enum MovementPlasmaMigrationStep {
  FRANKLIN_ONLY = "FRANKLIN_ONLY",
  /** PHASE_ONE - Bill getter confirmation FRANKLIN_AND_PLASMA. * */
  PHASE_ONE = "PHASE_ONE",
  /** PHASE_TWO - All confirmation without passcode FRANKLIN_AND_PLASMA. */
  PHASE_TWO = "PHASE_TWO",
  /** PHASE_THREE - All confirmation FRANKLIN_AND_PLASMA. */
  PHASE_THREE = "PHASE_THREE",
  PHASE_FOUR = "PHASE_FOUR",
  PHASE_FIVE = "PHASE_FIVE",
  PHASE_SIX = "PHASE_SIX",
  PLASMA_ONLY = "PLASMA_ONLY",
}

/**
 * Determine which phase of money movement we are on. Phases are inclusive of all previous phases.
 * We will document what each phase does as we use them, currently the only one we are going to
 * be using is STEP_ONE which will show that the movement from sender to Square is being
 * processed by money-mover.
 */
export enum MovementMoneyMoverMigrationStep {
  /** STEP_ONE - Movement is being processed by money-mover. * */
  STEP_ONE = "STEP_ONE",
  STEP_TWO = "STEP_TWO",
  STEP_THREE = "STEP_THREE",
  STEP_FOUR = "STEP_FOUR",
  STEP_FIVE = "STEP_FIVE",
}

/** Determine the state of the plasma flow that corresponds to the DbMovement.flowAttempt. */
export enum MovementFlowState {
  /** PENDING - We are about to start a new plasma flow for the given movement. */
  PENDING = "PENDING",
  /** IN_PROGRESS - Plasma flow was created. */
  IN_PROGRESS = "IN_PROGRESS",
  /** COMPLETED - Plasma flow has ended. */
  COMPLETED = "COMPLETED",
  /** COMPLETED_AND_ADVANCED - Plasma flow has ended and we advanced from waiting on customer. */
  COMPLETED_AND_ADVANCED = "COMPLETED_AND_ADVANCED",
}

/**
 * This is intended to be serialized to Fidelius as JSON.
 * The field names are meaningful and CANNOT be changed, unlike other protos
 * that are binary serialized between services.
 */
export interface GovernmentIdData {
  /** Primary number or identifier of government ID.  For example: a driver's license number. */
  id_number?: string;
  /** mm/dd/yyyy */
  birth_date?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  name_suffix?: string;
  address?: GlobalAddress;
  /** The expiration date of the id. */
  expiration_date?: string;
  /** The raw barcode text that produced this government ID data. */
  barcode_text?: string;
  /** An identifier of the document (e.g. a Document Discriminator (DD)). */
  document_number?: string;
}

export interface MergeDecision {
  /** Franklin customer token of the source customer who is being merged into the target customer. */
  source_customer_token?: string;
  /** Franklin customer token of the target customer who the source customer is merging into. */
  target_customer_token?: string;
  /**
   * Franklin customer token of the customer who initiated the merge.
   * Either the target or the source.
   */
  initiator_customer_token?: string;
  merge_reason?: MergeReason;
  merge_resolution?: MergeResolution;
  flow_token?: string;
  alias?: Alias;
  /** Unix time in millis */
  created_at?: number;
  /** Unix time in millis */
  merged_at?: number;
  /** If the customer is required to confirm the merge. */
  resolution_required?: boolean;
  /** Unix time in millis */
  resolved_at?: number;
  alias_verification_required?: boolean;
  /** Unix time in millis */
  alias_verified_at?: number;
  passcode_verification_required?: boolean;
  /** Unix time in millis */
  passcode_verified_at?: number;
  extra_verification_required?: boolean;
  name_confirmation_required?: boolean;
  extra_verification_pending?: boolean;
  merge_confirmation_pending?: boolean;
  magic_link_verification_required?: boolean;
  magic_link_verification_pending?: boolean;
}

export interface IdvSummary {
  customer_id?: string;
  customer_token?: string;
  idv_state?: IdvSummaryIdvState;
  pass_reason?: IdvSummaryPassReason;
  passed_at?: number;
  failure_reason?: IdvSummaryFailureReason;
  failed_at?: number;
  review_state?: IdvSummaryReviewState;
  review_requested_at?: number;
  review_completed_at?: number;
  case_id?: string;
  case_created_at?: number;
  /** @deprecated */
  verification_id?: string;
  verification_token?: string;
}

export enum IdvSummaryIdvState {
  /** ATTEMPTS_REMAINING - The customer has IDV attempts remaining. */
  ATTEMPTS_REMAINING = "ATTEMPTS_REMAINING",
  /** PASSED - The customer has passed IDV. The PassReason indicates why. */
  PASSED = "PASSED",
  /** IN_REVIEW - The customer's IDV data is in review. They are treated like they have passed. */
  IN_REVIEW = "IN_REVIEW",
  /** FAILED - The customer has failed IDV. The FailureReason indicates why. */
  FAILED = "FAILED",
  /** WAITING_ON_DOCUMENTS - The customer's IDV process has begun. Waiting for the documents to be uploaded. */
  WAITING_ON_DOCUMENTS = "WAITING_ON_DOCUMENTS",
}

export enum IdvSummaryReviewState {
  /**
   * PENDING - The customer has provided enough info to begin a manual review. A review case is created
   * when an IdvSummary is put into this state.
   */
  PENDING = "PENDING",
  /** APPROVED - The customer's info was approved by the reviewer. */
  APPROVED = "APPROVED",
  /** REJECTED - The customer's info was rejected by the reviewer. */
  REJECTED = "REJECTED",
  /**
   * INCONCLUSIVE - Prior to the launch of selfies, a review of the customer's info can be inconclusive if the
   * customer's info wasn't found, but a negative match of the SSN wasn't found either. When
   * selfies launch, all customer's in this state will be downgraded so that they can provide a
   * selfie and get re-reviewed.
   */
  INCONCLUSIVE = "INCONCLUSIVE",
  /**
   * NO_ACTION - It's possible for a review to be closed with no action taken. This can happen if a review
   * was accidentally created, or if something else changes that makes the review obsolete.
   */
  NO_ACTION = "NO_ACTION",
}

export enum IdvSummaryPassReason {
  /** FULL_MATCH - The customer's name, birthdate and SSN were fully matched. */
  FULL_MATCH = "FULL_MATCH",
  /** MANUAL_MATCH - The customer's name, birthdate and SSN were fully matched by a reviewer. */
  MANUAL_MATCH = "MANUAL_MATCH",
  /** SELFIE_MATCH - The customer's government ID was matched to a selfie. */
  SELFIE_MATCH = "SELFIE_MATCH",
  /**
   * PARTIAL_MATCH - The customer's name, birthdate and SSN were partially matched. This will no longer be used
   * after the Great Downgrade of 2017.
   */
  PARTIAL_MATCH = "PARTIAL_MATCH",
  /**
   * GOVERNMENT_ID_ONLY_MATCH - The customer's government ID was matched but they weren't required to provide a selfie
   * because they used an app that didn't support taking a selfie. Customers in this state are
   * subject to periodic re-verification.
   */
  GOVERNMENT_ID_ONLY_MATCH = "GOVERNMENT_ID_ONLY_MATCH",
  /**
   * DOCUMENT_MATCH - The customer provided an approved document or set of documents to verify their identity
   * for a previous attempt.
   */
  DOCUMENT_MATCH = "DOCUMENT_MATCH",
}

export enum IdvSummaryFailureReason {
  /**
   * ATTEMPTS_EXHAUSTED - The customer ran out of attempts. This is the legacy failure reason, and the result of
   * too many failures in clients that don't support government ID.
   */
  ATTEMPTS_EXHAUSTED = "ATTEMPTS_EXHAUSTED",
  /** GOVERNMENT_ID_DID_NOT_MATCH - The name and birthdate on the scanned government ID did not match previous attempts. */
  GOVERNMENT_ID_DID_NOT_MATCH = "GOVERNMENT_ID_DID_NOT_MATCH",
  /** NEGATIVE_SSN_SIGNAL - The SSN that they provided matches a different person. */
  NEGATIVE_SSN_SIGNAL = "NEGATIVE_SSN_SIGNAL",
  /** SELFIE_DID_NOT_MATCH - The selfie did not match the photo on the government ID. */
  SELFIE_DID_NOT_MATCH = "SELFIE_DID_NOT_MATCH",
  /** NEGATIVE_INTERNAL_RISK_SIGNAL - The ML evaluation of this idv request identified this case is potentially risky */
  NEGATIVE_INTERNAL_RISK_SIGNAL = "NEGATIVE_INTERNAL_RISK_SIGNAL",
}

/**
 * This proto is serialized with Gson for the frontend, any renames must be reflected in
 * admin-ng-situation.html as well.
 */
export interface SupportStats {
  new_email_case_count?: number;
  new_phone_case_count?: number;
  new_text_case_count?: number;
  open_email_case_count?: number;
  open_phone_case_count?: number;
  open_text_case_count?: number;
  new_email_case_max_age_sec?: number;
  new_phone_case_max_age_sec?: number;
  new_text_case_max_age_sec?: number;
  pending_case_count?: number;
  pending_case_max_age_sec?: number;
}

/** A device that can receive push notifications. */
export interface PushDevice {
  app_token?: string;
  device_token?: string;
  platform?: Platform;
  build_type?: BuildType;
  active?: boolean;
  /** Entity version for downstream write serialization. Not an "app" or device version. */
  version?: number;
}

/**
 * Encapsulates a digital wallet token from Banklin.  Digital wallet tokens are tokens issued
 * by providers such as Apple and Google.  They provide an abstraction over a customers PAN such
 * that when a transaction is created, the merchant involved in the transaction never has access
 * to the PAN.  The only parties with access to the PAN in Apple Pay's case would be Apple, Marqeta
 * and Square.
 */
export interface DigitalWalletTokenData {
  tokens?: DigitalWalletToken[];
}

/**
 * A setting which modifies the behavior of the app. Many feature flags map to server-side
 * experiments. This version of the proto includes data relevant to the admin dashboard, but
 * not the apps. It also excludes data relevant to the apps, but not the admin.
 */
export interface AdminFeatureFlag {
  /**
   * The programmatic name of this feature flag. For feature flags stored on the server,
   * this corresponds to the experiment name.
   */
  name?: string;
  /** A human readable description of this feature flag. */
  label?: string;
  /** The options available for this feature flag. */
  options?: AdminFeatureFlagOption[];
  /** True if this feature flag should be included in ClientData.feature_flags. */
  client_data_enabled?: boolean;
}

/** Each option describes a different way that the client should behave. */
export interface AdminFeatureFlagOption {
  /**
   * The programmatic name of this option. For feature flags stored on the server, this
   * corresponds to the experiment bucket.
   */
  value?: string;
  /** A human readable description of this option. */
  label?: string;
}

/** Used to serialize raw data read from government ID images. */
export interface RawGovernmentIdData {
  barcode_text?: string;
}

export interface CryptocurrencyTransfer {
  external_id?: string;
  target_wallet_address?: string;
  status?: CryptocurrencyTransferStatus;
  created_at?: number;
  updated_at?: number;
  amount?: Money;
  type?: CryptocurrencyTransferType;
  /** The fiat value of this transfer */
  settled_amount?: Money;
  /** The customer's crypto stored balance token */
  stored_balance_token?: string;
  /** The speed selected by customer at which they want their withdrawal to settle on blockchain */
  speed?: string;
  /** The fee paid by the customer to fund a withdrawal at the selected speed */
  fee?: Money;
}

export enum CryptocurrencyTransferStatus {
  PENDING = "PENDING",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

export enum CryptocurrencyTransferType {
  WITHDRAWAL = "WITHDRAWAL",
  DEPOSIT_REVERSAL = "DEPOSIT_REVERSAL",
}

/** Internal vitess information for developer use only */
export interface VitessShardInfo {
  keyspace?: string;
  shard?: string;
  developer_login_cmd?: string;
}

/** Vendor response details for identity verification requests, fetched from idmatch. */
export interface IdentityVerificationVendorResponse {
  /** Verbatim response from vendor. */
  vendor?: string;
  raw_response?: string;
}

/** Customers can be blocked in a waitlist when onboarding in the UK. */
export interface Waitlist {
}

export enum WaitlistStatus {
  WAITLISTED = "WAITLISTED",
  APPROVED = "APPROVED",
}

export enum WaitlistStatusChangeReason {
  ONBOARDING = "ONBOARDING",
  MANUAL = "MANUAL",
  INVITE = "INVITE",
  PAYMENT = "PAYMENT",
  APPROVE_ALL = "APPROVE_ALL",
  ALLOWLISTED_ALIAS = "ALLOWLISTED_ALIAS",
}

/** Risk can suggest that a customer should not be allowed to login. */
export interface LoginAttempt {
  customer_token?: string;
  app_token?: string;
  flow_token?: string;
  decision?: LoginAttemptDecision;
  created_at?: number;
  updated_at?: number;
  target_customer_token?: string;
}

export enum LoginAttemptDecision {
  DENIED = "DENIED",
  APPROVED = "APPROVED",
  MANUALLY_APPROVED = "MANUALLY_APPROVED",
  MANUALLY_DENIED = "MANUALLY_DENIED",
  CUSTOMER_DENIED = "CUSTOMER_DENIED",
  DENIED_WITHOUT_FALLBACK = "DENIED_WITHOUT_FALLBACK",
}

export interface Challenge {
  customer_token?: string;
  type?: string;
  created_at?: number;
  succeeded_at?: number;
  failed_at?: number;
}

/** Encapsulates the PII data related with a challenge. */
export interface ChallengePiiData {
  topic?: string;
  proof?: string;
}

/** Encapsulates PII data for global addresses. This proto is encrypted and persisted in the DB. */
export interface GlobalAddressPiiData {
  address?: GlobalAddress;
  is_vaulted?: boolean;
}

export interface EnhancedVerification {
}

/** Sources of customer verification information. */
export enum EnhancedVerificationSource {
  IDV = "IDV",
  GOVERNMENT_ID = "GOVERNMENT_ID",
  DEBIT_CARD_PHOTO = "DEBIT_CARD_PHOTO",
  CREDIT_CARD_PHOTO = "CREDIT_CARD_PHOTO",
  CONTACTS = "CONTACTS",
  LOCATION = "LOCATION",
  PLAID = "PLAID",
}

export interface EvaluateEnhancedVerificationEvent {
  enhanced_verification_source?: EnhancedVerificationSource;
  customer_token?: string;
  suspension_token?: string;
  payment_card?: PaymentCard;
  verification_token?: string;
  identityData?: CustomerIdentityData;
  contactData?: EvaluateEnhancedVerificationEventCustomerContactData;
}

export interface EvaluateEnhancedVerificationEventCustomerContactData {
  /** A list of the customers who have the suspended customer in their contacts. */
  customer_contacts?: EvaluateEnhancedVerificationEventCustomerContactDataContact[];
}

export interface EvaluateEnhancedVerificationEventCustomerContactDataContact {
  customer_token?: string;
  number_chargebacks?: number;
  number_payments_made?: number;
  payment_gpv?: number;
}

export interface InstrumentVerification {
  id?: string;
  instrument_link_id?: string;
  customer_id?: string;
  created_at?: number;
  updated_at?: number;
  method_type?: InstrumentVerificationSource;
  status?: InstrumentVerificationStatus;
}

export enum InstrumentVerificationSource {
  CARD_PHOTO = "CARD_PHOTO",
  PLAID = "PLAID",
  BUYER_API = "BUYER_API",
}

export enum InstrumentVerificationStatus {
  DENIED = "DENIED",
  APPROVED = "APPROVED",
  PENDING = "PENDING",
  INVALIDATED = "INVALIDATED",
  SKIPPED = "SKIPPED",
}

/** Wrapper of data from Plaid for debugging. */
export interface BalanceCheckAccountData {
  account_id?: string;
  item_id?: string;
  institution_id?: string;
  available_products?: string;
  item_error?: string;
  current_balance?: number;
  available_balance?: number;
  meta_name?: string;
  type?: string;
  subtype?: string;
}

export interface CustomerIdentityData {
  customer_token?: string;
  plaid_info?: CustomerIdentityDataInfo;
  cash_app_info?: CustomerIdentityDataInfo;
  idv_info?: CustomerIdentityDataInfo;
  government_id_info?: CustomerIdentityDataInfo;
  plaid_accounts?: CustomerIdentityDataPlaidAccount[];
  birthdate?: number;
  plaid_active_access_token?: string;
  plaid_active_account_id?: string;
  instrument_token?: string;
}

export interface CustomerIdentityDataInfo {
  names?: string[];
  emails?: CustomerIdentityDataInfoAlias[];
  phone_numbers?: CustomerIdentityDataInfoAlias[];
  addresses?: CustomerIdentityDataInfoAddress[];
  plaid_routing_numbers?: string[];
  customer_tokens?: CustomerIdentityDataInfoAlias[];
}

export interface CustomerIdentityDataInfoAlias {
  type?: CustomerIdentityDataInfoAliasAliasType;
  value?: string;
  primary?: boolean;
}

export enum CustomerIdentityDataInfoAliasAliasType {
  EMAIL = "EMAIL",
  PHONE = "PHONE",
  CUSTOMER_TOKEN = "CUSTOMER_TOKEN",
}

export interface CustomerIdentityDataInfoAddress {
  primary?: boolean;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface CustomerIdentityDataPlaidAccount {
  balance?: CustomerIdentityDataPlaidAccountBalance;
  meta?: CustomerIdentityDataPlaidAccountMeta;
  institution_type?: string;
  /** General account type (e.g. "depository" or "credit"). */
  type?: string;
  /** Account subtype (e.g. for type="depository", this could be "checking" or "savings"). */
  subtype?: string;
  account_id?: string;
  /** https://plaid.com/docs/api/accounts/#accounts-get-response-accounts-mask */
  mask?: string;
}

export interface CustomerIdentityDataPlaidAccountBalance {
  current?: Money;
  available?: Money;
}

export interface CustomerIdentityDataPlaidAccountMeta {
  name?: string;
  limit?: Money;
}

/** Additional information persisted on the DbTransferData object used to render blockers information. */
export interface TransferData {
  investment_order?: InvestmentOrder;
}

/** Encapsulates the PII data collected for name on card. */
export interface NameOnCardPiiData {
  name_on_card?: string;
}

export interface RegionLockingMovementBatch {
  /**
   * Movements owned by these customers will be region-locked sequentially in order from first to
   * last in the list.
   */
  customer_ids?: string[];
  /** Movements processed by this batch will be locked to the region specified in this field. */
  new_region?: string;
  /** Only movements created on this date or later will be updated. */
  start_date?: number;
  /** Only movements ending on this date or before will be updated. */
  end_date?: number;
  /** The total number of movements that should be updated across all batches. */
  estimated_total?: number;
  /**
   * The total number of movements that have been updated so far including individual totals for
   * each preceding batch. It doesn't include numbers for the current batch.
   * This field is used to keep track of progress, where
   * progress = total_updated_up_to_last_batch / estimated_total * 100.
   */
  total_updated_up_to_last_batch?: number;
}

export interface ThreeDomainSecureChallengeMetadata {
  strategy_type?: ThreeDomainSecureChallengeMetadataChallengeStrategyType;
  /** The country code associated to the card used to generate the challenge. */
  card_country_code?: string;
  /**
   * The 3DS2 authentication type returned by Panama, e.g frictionless, static, dynamic or OOB(out-of-band)
   * as specified in the 3DS2 spec as "authenticationType"
   */
  authentication_type?: string;
}

export enum ThreeDomainSecureChallengeMetadataChallengeStrategyType {
  /** PROACTIVE - Cash decided to proatively request 3DS authentication. */
  PROACTIVE = "PROACTIVE",
  /** SOFT_DECLINE - The authorisation was soft declined as 3DS authentication is required by the issuer. */
  SOFT_DECLINE = "SOFT_DECLINE",
}

export interface ThreeDomainSecureChallenge {
  /**
   * This url will be used to present a three domain secure blocker to the customer.
   * It only supports GET urls and it won't be filled in when the url returned by
   * Adyen must be sent via POST. Use redirect_url instead in order to support both
   * methods.
   *
   * @deprecated
   */
  challenge_url?: string;
  status?: ThreeDomainSecureChallengeStatus;
  /**
   * Identifies the payment being challenged. This blob is provided by Adyen after a payment
   * request gets challenged. It is used to submit the results of a 3DS challenge after as
   * customer has completed it.
   */
  payment_data?: string;
  /**
   * Represents a collection of additional parameters included in the 3DS challenge results from
   * the client.These params will be used to authenticate the payment.
   * Futher read on 3DS redirect:
   * https://docs.adyen.com/checkout/3d-secure/redirect-3ds2-3ds1/api-only?tab=codeBlock4ZAB6_Java#handle-the-redirect-web
   */
  details?: { [key: string]: string };
  /**
   * Additional 3DS-specific information product of a successful 3DS authentication. It can be
   * used by the acquirer to authorise a payment.
   * Set when status=SUCCESS.
   */
  result?: ThreeDomainSecureResult;
  /** Stores if the redirect message has been resolved. */
  redirect_message_resolved?: boolean;
  /** Structured version of a redirect URL that includes method, base URI, and parameters. */
  redirect_url?: ThreeDomainSecureRedirectUrl;
  /**
   * Flag to identify if associated payment have its challenge skipped
   * Deprecated since all remaining use cases were changed to unresolveable blockers instead of skipping 3DS.
   *
   * @deprecated
   */
  skip_challenge?: boolean;
  /** Action fields necessary for clients to be able to handle 3DSv2 challenges. */
  action?: ThreeDomainSecureAction;
  /** Defined as string and not enum in order to avoid breaking clients when new values are needed. e.g. PAYMENT, CARD_LINK */
  transaction_type?: string;
  /** e.g. payment_token, instrument_token, etc. */
  transaction_id?: string;
  /** Metadata about the challenge. */
  metadata?: ThreeDomainSecureChallengeMetadata;
}

export enum ThreeDomainSecureChallengeStatus {
  /**
   * NOT_APPLICABLE - 3DS authentication not required for customer. This could because it's not an EU payment
   * or because the payments provider indicated it so.
   */
  NOT_APPLICABLE = "NOT_APPLICABLE",
  /** PENDING - Customer required by issuer to submit and pass challenge. */
  PENDING = "PENDING",
  /** COMPLETED - Customer has completed challenge. */
  COMPLETED = "COMPLETED",
  /** FAILED - Customer has failed challenge. */
  FAILED = "FAILED",
  /** SUCCESS - The customer has passed the challenge. */
  SUCCESS = "SUCCESS",
  /** ERROR - An unexpected error has occurred. */
  ERROR = "ERROR",
}

export interface ThreeDomainSecureChallengeDetailsEntry {
  key: string;
  value: string;
}

export interface ThreeDomainSecureResult {
  /** From the paResponse from the issuer's Access Control System. */
  autentication_response?: string;
  /** The enrollment response from the VERes message from the Directory Server. */
  directory_response?: string;
  /** The authentication value for the 3D Secure authentication session. The returned value is a base64-encoded 20-byte array. */
  cavv?: string;
  /** The CAVV Algorithmn used during the authentication. */
  cavv_algorithm?: string;
  /** The transaction identifier assigned by the Directory Server (base64 encoded, 20 bytes in a decoded form). */
  xid?: string;
  /** The electronic commerce indicator. */
  eci?: string;
  /** Proof of 3DS2 authentication. */
  authentication_value?: string;
  /** The unique transaction identifier assigned by the Directory Server to identify a single 3DS2 transaction. */
  ds_trans_id?: string;
  /** The version of the 3D Secure protocol. */
  three_ds_version?: string;
}

export interface ThreeDomainSecureAction {
  /** The type of action the client must perform in order proceed with a payment. */
  actionType?: ThreeDomainSecureActionActionType;
  /**
   * Base64-encoded json string of action object returned by Adyen. This data will be returned
   * to the client and passed through to Adyen's client SDK components.
   */
  action_data?: string;
  /**
   * Base64-encoded json string with action details provided by Adyen client SDK components. This
   * data will be used to attempt to complete a 3DSv2 challenge.
   */
  action_details?: string;
}

export enum ThreeDomainSecureActionActionType {
  UNKNOWN = "UNKNOWN",
  /** REDIRECT - The client must redirect the customer to a website in order to authenticate them. */
  REDIRECT = "REDIRECT",
  /** THREEDS2FINGERPRINT - A 3DSv2 fingerprint of the device must be sent by the client. */
  THREEDS2FINGERPRINT = "THREEDS2FINGERPRINT",
  /** THREEDS2CHALLENGE - The client must be authenticated via a 3DSv2 challenge. */
  THREEDS2CHALLENGE = "THREEDS2CHALLENGE",
}
